import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import './BookingFlow.css';
import { API_URL, CENTER_DATA, RECAPTCHA_SITE_KEY, RESERVATION_TIMEOUT } from './booking/constants';
import {
  formatCountdown, getServicePrice, stripHtml, truncateText, getTherapistName,
  formatPhoneInput, formatDayName, formatMonthYear, formatTime,
  isSameDay, isToday, isPast, groupSlotsByPeriod, getRecommendedTimes, getWeekDays
} from './booking/utils';
import LocationStep from './booking/LocationStep';
import GuestCountStep from './booking/GuestCountStep';
import CategoryGridStep, { categoryNeedsAnatomy } from './components/booking/CategoryGridStep';
import RemovalDecisionStep from './booking/RemovalDecisionStep';
import ServiceDetailModal from './booking/ServiceDetailModal';
import GuestListStep from './booking/GuestListStep';
import RemovalTypeStep from './booking/RemovalTypeStep';
import TherapistStep from './booking/TherapistStep';
import CartSummaryStep from './booking/CartSummaryStep';
import ServiceSelectionStep from './booking/ServiceSelectionStep';
import TimeSelectionStep from './booking/TimeSelectionStep';
import ConfirmStep from './booking/ConfirmStep';
import SuccessStep from './booking/SuccessStep';
import AuthForms from './booking/AuthForms';
import BookingHeader from './booking/BookingHeader';
import ProgressIndicator from './booking/ProgressIndicator';
import StickyNav from './booking/StickyNav';
import AccountPage from './booking/AccountPage';
import GiftCardPage from './booking/GiftCardPage';
import PackagesPage from './booking/PackagesPage';
import ReferralsPage from './booking/ReferralsPage';
import QuickRebookBar from './booking/QuickRebookBar';
import GuidedAddOnsFlow from './booking/GuidedAddOnsFlow';

// source: booking-origin stamp for the mirror ('WebStore' = online site, 'kiosk' = in-salon kiosk).
function BookingFlow({ source = 'WebStore' }) {
  const [showQuickRebookBar, setShowQuickRebookBar] = useState(() => {
    // Honor the session-level dismiss flag at the parent level too — keeps the
    // banner from flashing through on remount before QuickRebookBar's inner
    // sessionStorage check runs.
    try {
      return sessionStorage.getItem('welcomeBannerDismissed') !== 'true';
    } catch {
      return true;
    }
  });
  const [showQuickRebookModal, setShowQuickRebookModal] = useState(false);
  const [isRebooking, setIsRebooking] = useState(false);
  // URL routing — map steps to paths so browser back/forward works
  const STEP_TO_PATH = {
    1: '/', 2: '/guests', 3: '/guests',
    3.5: '/categories',
    4: '/services', 5: '/services', 6: '/services', 7: '/services',
    8: '/providers', 8.5: '/providers',
    9: '/time', 9.5: '/time',
    10: '/confirm', 11: '/success',
    12: '/account', 13: '/gift-cards', 14: '/packages', 15: '/referrals'
  };
  const PATH_TO_STEP = {
    '/': 1, '/guests': 2, '/categories': 3.5, '/services': 4, '/providers': 8,
    '/time': 9, '/confirm': 10, '/success': 11,
    '/account': 12, '/gift-cards': 13, '/packages': 14, '/referrals': 15
  };

  function resolvePathToStep(path) {
    if (path === '/anatomy') return 3.5; // legacy URL → category grid
    if (path && path.startsWith('/services/')) return 4;
    return PATH_TO_STEP[path] || 1;
  }

  const [step, setStep] = useState(() => {
    return resolvePathToStep(window.location.pathname);
  });
  const isPopStateRef = useRef(false);
  const signInModalRef = useRef(false);

  // Listen for browser back/forward
  useEffect(() => {
    window.history.replaceState({ step }, '', window.location.pathname);
    const handlePopState = (e) => {
      // If sign-in modal is open, back button just closes it
      if (signInModalRef.current) {
        signInModalRef.current = false;
        setShowSignInModal(false);
        setSignInFromNav(false);
        return;
      }
      if (e.state?.step) {
        isPopStateRef.current = true;
        setStep(e.state.step);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Sync step changes to URL.
  // NOTE: selectedCategory is declared later in this component, so we cannot
  // include it in the dep array (TDZ ReferenceError at render time). The effect
  // callback runs after render, so reading selectedCategory inside the body is safe.
  // step changes always co-occur with category changes from CategoryGridStep, so
  // depending on step alone is sufficient to keep the URL in sync.
  useEffect(() => {
    if (isPopStateRef.current) {
      isPopStateRef.current = false;
      return;
    }
    let newPath = STEP_TO_PATH[step] || '/';
    if (step === 4 && selectedCategory?.id) {
      newPath = `/services/${selectedCategory.id}`;
    }
    if (newPath !== window.location.pathname) {
      window.history.pushState({ step }, '', newPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Scroll to top on every step change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [centers, setCenters] = useState([]);
  const [categories, setCategories] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [loadingTherapists, setLoadingTherapists] = useState(false);

  const [selectedCenter, setSelectedCenter] = useState(null);
  const [guestCount, setGuestCount] = useState(1);
  const [currentGuestIndex, setCurrentGuestIndex] = useState(0);
  const [guestSelections, setGuestSelections] = useState([]);
  
  // Group booking form state (7+ guests, West Van only)
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [groupFormName, setGroupFormName] = useState('');
  const [groupFormPhone, setGroupFormPhone] = useState('');
  const [groupFormEmail, setGroupFormEmail] = useState('');
  const [groupFormOccasion, setGroupFormOccasion] = useState('');
  const [groupFormGuestCount, setGroupFormGuestCount] = useState('');
  const [groupFormTime, setGroupFormTime] = useState('');
  const [groupFormServices, setGroupFormServices] = useState('');
  const [groupFormSubmitting, setGroupFormSubmitting] = useState(false);
  
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState('all');
  const [expandedAccordion, setExpandedAccordion] = useState(null);
  const [selectedService, setSelectedService] = useState(null); // used for current mandatory service in steps 6/7
  const [detailModalService, setDetailModalService] = useState(null);
  const [conflictWarning, setConflictWarning] = useState(null);
  const [selectedTherapist, setSelectedTherapist] = useState(null); // legacy, kept for compatibility

  // Toggle-to-cart state (kiosk pattern)
  const [selectedServices, setSelectedServices] = useState([]);       // services toggled in step 4
  const [serviceAddOns, setServiceAddOns] = useState({});             // { [serviceId]: { optional: [], mandatory: [] } }
  const [mandatoryServices, setMandatoryServices] = useState([]);     // services with mandatory add-ons
  const [currentMandatoryIdx, setCurrentMandatoryIdx] = useState(0);  // which mandatory service we're asking about
  const [therapistsByService, setTherapistsByService] = useState({});  // { [serviceId]: therapist[] }
  const [selectedTherapists, setSelectedTherapists] = useState({});    // { [serviceId]: therapist }
  const [proceedingFromServices, setProceedingFromServices] = useState(false); // loading state
  const [pendingRebookAddOns, setPendingRebookAddOns] = useState(false); // triggers add-on flow after rebook sets services

  // Time selection state
  const [weekStart, setWeekStart] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [futureDays, setFutureDays] = useState([]);
  // Service IDs whose provider preference has been temporarily relaxed for slot search
  const [relaxedServices, setRelaxedServices] = useState([]);
  const relaxedFirstRunRef = useRef(true);

  // Auth state
  const [authMode, setAuthMode] = useState('phone'); // 'phone', 'otp', 'signup', 'password', 'multipleAccounts'
  const [authPhone, setAuthPhone] = useState('');
  const [authOtp, setAuthOtp] = useState('');
  const [authVerificationId, setAuthVerificationId] = useState('');
  const [authUserName, setAuthUserName] = useState('');
  const [authLoginUserName, setAuthLoginUserName] = useState(''); // The actual username Zenoti expects for login
  const [authIntent, setAuthIntent] = useState('otp'); // 'otp' or 'password' — what the user chose on the phone screen
  const [authFirstName, setAuthFirstName] = useState('');
  const [authLastName, setAuthLastName] = useState('');
  const [authEmail, setAuthEmail] = useState('');
  const [authDob, setAuthDob] = useState('');
  const [authGender, setAuthGender] = useState('');
  const [authReferralCode, setAuthReferralCode] = useState(() => {
    try { const p = new URLSearchParams(window.location.search); return p.get('ref') || ''; } catch { return ''; }
  });
  const [authHowHeard, setAuthHowHeard] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authConfirmPassword, setAuthConfirmPassword] = useState('');
  const [authMarketingEmail, setAuthMarketingEmail] = useState(true);
  const [authMarketingSms, setAuthMarketingSms] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetUserId, setResetUserId] = useState('');
  const [resetVerificationId, setResetVerificationId] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [guestInfo, setGuestInfo] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('booking-guest')); } catch { return null; }
  }); // { guestId, firstName, lastName, email, phone }
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [unfilledForms, setUnfilledForms] = useState([]);
  const [bookingNotes, setBookingNotes] = useState('');
  const [upkeepApplied, setUpkeepApplied] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [reserveData, setReserveData] = useState(null);
  const [reserving, setReserving] = useState(false);
  const [invoiceTotals, setInvoiceTotals] = useState(null); // { subtotal, discount, tax, total }
  const [accountData, setAccountData] = useState(null);
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountTab, setAccountTab] = useState('upcoming');
  const [signInFromNav, setSignInFromNav] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);

  // Close sign-in modal and pop the history entry it pushed
  const closeSignInModal = () => {
    if (signInModalRef.current) {
      signInModalRef.current = false;
      window.history.back();
    }
    setShowSignInModal(false);
    setSignInFromNav(false);
  };

  // Policy agreement checkbox (confirm page)
  const [policyAgreed, setPolicyAgreed] = useState(false);

  const isGcPaidPopup = typeof window !== 'undefined' && !!window.opener && window.location.search.includes('gc_paid=1');
  const isPkgPaidPopup = typeof window !== 'undefined' && !!window.opener && window.location.search.includes('pkg_paid=1');

  // Persist guest info to sessionStorage so it survives page reloads
  useEffect(() => {
    if (guestInfo) sessionStorage.setItem('booking-guest', JSON.stringify(guestInfo));
    else sessionStorage.removeItem('booking-guest');
  }, [guestInfo]);

  // Reservation expiry timer (confirm page)
  const [reservationSeconds, setReservationSeconds] = useState(null);

  // Auto-advance toast (time selection)
  const [showAutoAdvanceToast, setShowAutoAdvanceToast] = useState(false);
  const [autoAdvanceDate, setAutoAdvanceDate] = useState(null);

  // Anatomy filter state
  const [anatomyFilter, setAnatomyFilter] = useState(null); // 'V' or 'P' — null until user chooses (modal-driven for anatomy categories)

  // Add-on state
  const [selectedOptionalAddOns, setSelectedOptionalAddOns] = useState([]);
  const [needsRemoval, setNeedsRemoval] = useState(null);
  const [selectedMandatoryAddOns, setSelectedMandatoryAddOns] = useState([]);
  const [useGuidedFlow, setUseGuidedFlow] = useState(false);
  const [addOnServices, setAddOnServices] = useState([]); // services that need add-on selection this pass

  const [loadingCategoryServices, setLoadingCategoryServices] = useState(false);
  const [categoryServices, setCategoryServices] = useState([]);

  // Catalog cache — pre-fetched for all centers on page load
  const [serviceSearch, setServiceSearch] = useState('');
  const [catalogCache, setCatalogCache] = useState({});  // { [centerId]: catalogData }
  const allCenterServices = selectedCenter ? catalogCache[selectedCenter.id] || null : null;

  useEffect(() => {
    setCenters(Object.values(CENTER_DATA));
    // Pre-fetch full catalog for all centers on page load (one request per center)
    Object.values(CENTER_DATA).forEach(center => {
      fetch(`${API_URL}/api/kiosk/centers/${center.id}/full-catalog`)
        .then(res => res.json())
        .then(data => {
          setCatalogCache(prev => ({ ...prev, [center.id]: data }));
        })
        .catch(err => console.error(`Failed to pre-fetch catalog for ${center.name}:`, err));
    });
  }, []);

  // Load therapists for all selected services when entering step 8
  // Use per-service cached data (has pricing from sync), fallback to live API
  useEffect(() => {
    if (step === 8 && selectedServices.length > 0 && selectedCenter) {
      setLoadingTherapists(true);
      const byService = {};
      const needsFetch = []; // services not in cache

      // Check cached per-service therapists first
      selectedServices.forEach(svc => {
        const cached = allCenterServices?.therapists?.[svc.id];
        if (cached && cached.length > 0) {
          byService[svc.id] = cached;
        } else {
          needsFetch.push(svc);
        }
      });

      if (needsFetch.length === 0) {
        // All services have cached therapist data with pricing
        setTherapistsByService(byService);
        // Don't pre-select — let the customer choose for each service
        setLoadingTherapists(false);
      } else {
        // Fetch missing services live from API
        Promise.all(needsFetch.map(async (svc) => {
          try {
            const url = `${API_URL}/api/centers/${selectedCenter.id}/catalog-therapists?serviceId=${svc.id}`;
            const resp = await fetch(url);
            const data = await resp.json();
            byService[svc.id] = data.therapists || [];
          } catch (e) {
            byService[svc.id] = allCenterServices?.allTherapists || [];
          }
        })).then(() => {
          setTherapistsByService(byService);
          // Don't pre-select — let the customer choose for each service
          setLoadingTherapists(false);
        });
      }
    }
  }, [step]);

  // Initialize time selection when entering step 9
  useEffect(() => {
    if (step === 9) {
      initTimeSelection();
    }
  }, [step]);

  // Reserve slot + apply coupons when entering step 10
  useEffect(() => {
    if (step === 10 && !reserveData && !reserving) {
      reserveSlot();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Reservation expiry timer — 5 minutes on the confirm page
  // Auto-cancels the reservation in Zenoti when time runs out
  useEffect(() => {
    if (step === 10) {
      setPolicyAgreed(false);
      setReservationSeconds(RESERVATION_TIMEOUT);
      const interval = setInterval(() => {
        setReservationSeconds(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setReservationSeconds(null);
    }
  }, [step]);

  // Auto-cancel in Zenoti the moment the timer expires
  useEffect(() => {
    if (reservationSeconds === 0 && reserveData?.invoiceId) {
      fetch(`${API_URL}/api/booking/cancel-expired`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoiceId: reserveData.invoiceId })
      }).catch(() => {});
    }
  }, [reservationSeconds, reserveData]);

  // Auto-dismiss toast after 5 seconds
  useEffect(() => {
    if (showAutoAdvanceToast) {
      const timer = setTimeout(() => {
        setShowAutoAdvanceToast(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showAutoAdvanceToast]);

  const loadCategories = useCallback(async (centerId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/centers/${centerId}/categories`);
      const data = await response.json();
      const allCats = data.categories || [];
      
      // Lightweight check: just get service count per category (no add-on fetching)
      const results = await Promise.all(
        allCats.map(async (cat) => {
          try {
            const r = await fetch(`${API_URL}/api/centers/${centerId}/categories/${cat.id}/service-count`);
            const d = await r.json();
            return { ...cat, _serviceCount: d.count };
          } catch (e) {
            return { ...cat, _serviceCount: -1 };
          }
        })
      );
      
      // Keep categories with services (or where check failed)
      const catsWithServices = results.filter(c => c._serviceCount !== 0);
      
      setCategories(catsWithServices);
      return catsWithServices;
    } catch (err) {
      setError('Failed to load categories.');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  async function loadServicesForCategory(category, centerId) {
    setLoadingCategoryServices(true);
    setCategoryServices([]);
    try {
      const response = await fetch(`${API_URL}/api/centers/${centerId}/categories/${category.id}/services`);
      const data = await response.json();
      
      // Filter: remove add-ons, variant parents, and services not available for online booking
      const services = (data.services || []).filter(s => {
        if (s.add_ons_info?.is_add_on) return false;
        if (s.duration === 0 && (s.price_info?.sale_price || 0) === 0) return false;
        if (s.catalog_info?.show_in_catalog === false) return false;
        if (s.catalog_info?.can_book === false) return false;
        return true;
      });

      setCategoryServices(services);
    } catch (err) {
      setError(`Failed to load services for ${category.name}.`);
    } finally {
      setLoadingCategoryServices(false);
    }
  }

  async function loadTherapistsForService(centerId, serviceId) {
    setLoadingTherapists(true);
    setTherapists([]);
    try {
      const url = `${API_URL}/api/centers/${centerId}/catalog-therapists?serviceId=${serviceId}`;
      const response = await fetch(url);
      const data = await response.json();
      setTherapists(data.therapists || []);
    } catch (err) {
      setError('Failed to load therapists.');
    } finally {
      setLoadingTherapists(false);
    }
  }

  // Get therapist-specific price (synced from Zenoti per therapist per service)
  // Falls back to base service price if therapist has no custom price
  function getTherapistPrice(service, therapist) {
    if (!service || !therapist || therapist.Id === 'any' || therapist.id === 'any') {
      return getServicePrice(service);
    }
    // Therapist has a custom price field from the sync cache
    const customPrice = therapist.Price || null;
    return customPrice !== null ? customPrice : getServicePrice(service);
  }

  // Get unique sub-categories from filtered services
  function getSubCategories() {
    const filtered = getFilteredServices();
    const subcats = new Set();
    filtered.forEach(service => {
      const subcat = service.sub_category_name || service.catalog_info?.sub_category_name || '';
      if (subcat) subcats.add(subcat);
    });
    return Array.from(subcats);
  }

  // Get services filtered by both anatomy AND sub-category, sorted by display_order
  function getDisplayServices() {
    let filtered = getFilteredServices();
    if (selectedSubCategory && selectedSubCategory !== 'all') {
      filtered = filtered.filter(s => {
        const subcat = s.sub_category_name || s.catalog_info?.sub_category_name || '';
        return subcat === selectedSubCategory;
      });
      return [...filtered].sort((a, b) => (a.display_order ?? 9999999) - (b.display_order ?? 9999999));
    }
    // "All" view: group services by sub-category, then sort within each group by display_order.
    // Sub-categories themselves render in the order their first service appears (by display_order).
    const sortedAll = [...filtered].sort((a, b) => (a.display_order ?? 9999999) - (b.display_order ?? 9999999));
    const groups = new Map(); // Map preserves insertion order
    sortedAll.forEach(svc => {
      const sub = svc.sub_category_name || svc.catalog_info?.sub_category_name || '';
      if (!groups.has(sub)) groups.set(sub, []);
      groups.get(sub).push(svc);
    });
    const result = [];
    for (const services of groups.values()) {
      result.push(...services);
    }
    return result;
  }

  // Group services by sub_category_name (using filtered list), sorted by display_order
  function getServiceGroups() {
    const filtered = getFilteredServices();
    const groups = {};
    filtered.forEach(service => {
      const subcat = service.sub_category_name || service.catalog_info?.sub_category_name || 'Services';
      if (!groups[subcat]) groups[subcat] = [];
      groups[subcat].push(service);
    });
    // Sort services within each group by display_order
    return Object.entries(groups).map(([name, services]) => ({
      name,
      services: services.sort((a, b) => (a.display_order ?? 9999999) - (b.display_order ?? 9999999))
    }));
  }

  // Get catalog for a center (from pre-fetched cache, or fetch on demand)
  async function fetchFullCatalog(centerId) {
    const cid = centerId || selectedCenter?.id;
    if (!cid) return null;
    // Return from cache if already loaded
    if (catalogCache[cid]) return catalogCache[cid];
    try {
      const res = await fetch(`${API_URL}/api/kiosk/centers/${cid}/full-catalog`);
      const data = await res.json();
      setCatalogCache(prev => ({ ...prev, [cid]: data }));
      return data;
    } catch (err) {
      console.error('Failed to fetch full catalog:', err);
      return null;
    }
  }

  // Handle search input — catalog is already loaded eagerly at center select
  function handleServiceSearch(query) {
    setServiceSearch(query);
  }

  // Get search results from full catalog (cross-category)
  function getSearchResults() {
    if (!serviceSearch || !allCenterServices) return [];
    const all = Object.values(allCenterServices.services || {}).flat();
    // Apply anatomy filter
    let filtered = anatomyFilter ? all.filter(s => {
      const code = (s.code || '').toUpperCase();
      if (code.startsWith('V-')) return anatomyFilter === 'V';
      if (code.startsWith('P-')) return anatomyFilter === 'P';
      return true;
    }) : all;
    // Apply text search
    const q = serviceSearch.toLowerCase();
    filtered = filtered.filter(s =>
      (s.name || '').toLowerCase().includes(q) ||
      (s.sub_category_name || '').toLowerCase().includes(q)
    );
    return filtered.sort((a, b) => (a.display_order ?? 9999999) - (b.display_order ?? 9999999));
  }

  // =====================
  // STEP FLOW:
  // 1 = Location
  // 2 = Guest Count
  // 2.5 = Anatomy Filter (V / P)
  // 3 = Guest List (multi-guest)
  // 4 = Service Selection
  // 5 = Optional Add-Ons
  // 6 = Needs Removal? (mandatory add-on yes/no)
  // 7 = Select Removal Type
  // 8 = Therapist
  // 8.5 = Service Summary / Add Another?
  // 9 = Time
  // 10 = Confirm
  // =====================

  // Multi-service cart for current guest
  const [serviceCart, setServiceCart] = useState([]);
  const [showAddAnother, setShowAddAnother] = useState(false);

  // Upkeep discount eligibility — computed from service cart + guest history
  const UPKEEP_WEEKS = 6;
  const upkeepEligibility = useMemo(() => {
    if (!accountData?.recentVisits?.length || !serviceCart.length) {
      console.log('[UPKEEP] Skipped:', !accountData?.recentVisits?.length ? 'no recentVisits' : 'no serviceCart',
        '| recentVisits count:', accountData?.recentVisits?.length || 0,
        '| serviceCart count:', serviceCart.length,
        '| cart names:', serviceCart.map(i => i.service.display_name || i.service.name));
      return [];
    }
    const now = new Date();
    const eligible = serviceCart
      .filter(item => {
        const cat = (getCategoryForService(item.service.id) || item.service.catalog_info?.category_name || '').toLowerCase();
        const svcDisplayName = (item.service.display_name || item.service.name || '').toLowerCase();
        const pass = cat.includes('wax') || cat.includes('brow') || cat.includes('lash')
          || svcDisplayName.includes('wax') || svcDisplayName.includes('brow') || svcDisplayName.includes('lash');
        console.log('[UPKEEP] Category check:', item.service.display_name || item.service.name, '| category:', cat, '| name:', svcDisplayName, '| pass:', pass);
        return pass;
      })
      .map(item => {
        const svcName = (item.service.display_name || item.service.name || '').toLowerCase();
        console.log('[UPKEEP] Looking for:', svcName, 'in', accountData.recentVisits.length, 'visits');
        for (const visit of accountData.recentVisits) {
          const visitNames = (visit.services || []).map(s => (s.name || '').toLowerCase());
          const match = visitNames.includes(svcName);
          console.log('[UPKEEP]   Visit', visit.date, '| services:', visitNames.join(', '), '| match:', match);
          if (match && visit.date) {
            const lastDate = new Date(visit.date);
            const deadline = new Date(lastDate);
            deadline.setDate(deadline.getDate() + UPKEEP_WEEKS * 7);
            if (deadline > now) {
              console.log('[UPKEEP] ✅ Eligible:', svcName, '| deadline:', deadline.toISOString());
              return { serviceName: item.service.display_name || item.service.name, lastDate: lastDate.toISOString(), deadline: deadline.toISOString() };
            } else {
              console.log('[UPKEEP] ❌ Past deadline:', deadline.toISOString());
            }
          }
        }
        return null;
      })
      .filter(Boolean);
    console.log('[UPKEEP] Result:', eligible);
    return eligible;
  }, [serviceCart, accountData, categories, allCenterServices]);

  function getNextStepAfterService(service) {
    const hasOptional = service.optional_add_ons?.length > 0;
    const hasMandatory = service.mandatory_add_ons?.length > 0;
    
    if (hasOptional) return 5;       // Show optional add-ons
    if (hasMandatory) return 6;      // Ask "needs removal?"
    return 8;                         // Go straight to therapist
  }

  function getNextStepAfterOptional(service) {
    const hasMandatory = service.mandatory_add_ons?.length > 0;
    if (hasMandatory) return 6;
    return 8;
  }

  // Filter out non-bookable services (add-ons, $0 headers, hidden)
  function filterBookableServices(services) {
    return (services || []).filter(s => {
      if (s.add_ons_info?.is_add_on) return false;
      if (s.duration === 0 && (s.price_info?.sale_price || 0) === 0) return false;
      if (s.catalog_info?.show_in_catalog === false) return false;
      if (s.catalog_info?.can_book === false) return false;
      return true;
    });
  }

  async function handleCenterSelect(center) {
    setSelectedCenter(center);
    setStep(2); // Navigate immediately
    // Use pre-fetched catalog if available (no API call at all)
    const cached = catalogCache[center.id];
    const data = cached || await fetchFullCatalog(center.id);
    if (data) {
      const cats = data.categories || [];
      setCategories(cats);
      if (cats.length > 0) {
        setSelectedCategory(cats[0]);
        setCategoryServices(filterBookableServices(data.services[cats[0].id] || []));
      }
    } else {
      // Fallback to individual calls if catalog not available
      loadCategories(center.id).then(cats => {
        if (cats.length > 0) {
          setSelectedCategory(cats[0]);
          loadServicesForCategory(cats[0], center.id);
        }
      });
    }
  }

  function handleGuestCountSelect(count) {
    // Handle 7+ group bookings
    if (count === '5+') {
      setGuestCount('5+');
      setShowGroupForm(true);
      return;
    }
    
    setGuestCount(count);
    setGuestSelections(Array(count).fill(null).map(() => ({
      services: [],
      therapist: null,
      anatomyFilter: null // per-guest anatomy choice
    })));
    setServiceCart([]);
    setCurrentGuestIndex(0);
    if (count === 1) {
      setTimeout(() => setStep(3.5), 300); // single guest → category grid
    } else {
      setTimeout(() => setStep(3), 300); // multi-guest → guest list
    }
  }

  async function handleGroupFormSubmit() {
    if (!groupFormName || !groupFormPhone || !groupFormGuestCount) {
      setError('Please fill in all required fields');
      return;
    }
    
    setGroupFormSubmitting(true);
    try {
      const formData = {
        location: selectedCenter?.name || '',
        name: groupFormName,
        phone: groupFormPhone,
        email: groupFormEmail,
        occasion: groupFormOccasion,
        guestCount: groupFormGuestCount,
        preferredTime: groupFormTime,
        services: groupFormServices
      };

      // Send form via email
      const res = await fetch(`${API_URL}/api/group-booking-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await res.json();
      if (data.success) {
        setStep(11);
        setBookingConfirmed(true);
        setGuestInfo({ email: groupFormPhone, firstName: groupFormName });
        // Reset form
        setShowGroupForm(false);
        setGroupFormName('');
        setGroupFormPhone('');
        setGroupFormEmail('');
        setGroupFormOccasion('');
        setGroupFormGuestCount('');
        setGroupFormTime('');
        setGroupFormServices('');
      } else {
        setError(data.error || 'Failed to submit group booking inquiry');
      }
    } catch (err) {
      setError('Failed to submit group booking inquiry. Please email info@cutiepiewaxbar.com directly.');
    } finally {
      setGroupFormSubmitting(false);
    }
  }

  // Filter services based on anatomy selection
  function getFilteredServices() {
    if (!anatomyFilter) return categoryServices;
    return categoryServices.filter(s => {
      const code = (s.code || '').toUpperCase();
      // If service has a V- or P- prefix, only show if it matches the filter
      if (code.startsWith('V-')) return anatomyFilter === 'V';
      if (code.startsWith('P-')) return anatomyFilter === 'P';
      // Services without a prefix are unisex — show for everyone
      return true;
    });
  }

  function handleGuestInfoChange(guestIndex, field, value) {
    setGuestSelections(prev => prev.map((guest, idx) => {
      if (idx !== guestIndex) return guest;
      return { ...guest, [field]: value };
    }));
  }

  function handleCopyServicesToAll(sourceGuestIndex) {
    const source = guestSelections[sourceGuestIndex];
    if (!source?.services?.length) return;
    const newSelections = guestSelections.map((guest, idx) => {
      if (idx === sourceGuestIndex) return guest;
      return {
        ...guest,
        services: source.services.map(item => ({ ...item })),
        anatomyFilter: source.anatomyFilter
      };
    });
    setGuestSelections(newSelections);
  }

  function handleAddServicesForGuest(guestIndex) {
    setCurrentGuestIndex(guestIndex);
    // Load existing cart for this guest
    const existingCart = guestSelections[guestIndex]?.services || [];
    setServiceCart(existingCart);
    // Restore selectedServices from cart items
    setSelectedServices(existingCart.map(item => item.service));
    setSelectedService(null);
    setSelectedOptionalAddOns([]);
    setNeedsRemoval(null);
    setSelectedMandatoryAddOns([]);
    setSelectedTherapist(null);
    setSelectedTherapists({});
    
    // If guest already picked anatomy and has services, go straight to services
    if (guestSelections[guestIndex]?.anatomyFilter && guestSelections[guestIndex]?.services?.length > 0) {
      setAnatomyFilter(guestSelections[guestIndex].anatomyFilter);
      if (!selectedCategory && categories.length > 0) {
        setSelectedCategory(categories[0]);
        if (allCenterServices?.services) {
          setCategoryServices(filterBookableServices(allCenterServices.services[categories[0].id] || []));
        } else {
          loadServicesForCategory(categories[0], selectedCenter.id);
        }
      }
      setTimeout(() => setStep(4), 300);
    } else {
      // New guest — show category grid first
      setAnatomyFilter(null);
      setTimeout(() => setStep(3.5), 300);
    }
  }

  function handleCategorySelect(category) {
    setSelectedCategory(category);
    setSelectedSubCategory('all');
    setExpandedAccordion(null);
    // Read from cached catalog (instant) instead of API call
    if (allCenterServices?.services) {
      setCategoryServices(filterBookableServices(allCenterServices.services[category.id] || []));
    } else {
      setCategoryServices([]);
      loadServicesForCategory(category, selectedCenter.id);
    }
  }

  // Tile tap on the category grid → set selectedCategory to the matched Zenoti category and proceed to services.
  function handleCategoryTap(staticCat, zenotiCategory) {
    if (!zenotiCategory) return;
    handleCategorySelect(zenotiCategory);
    setStep(4);
  }

  // True iff the currently-selected category has both V- and P-prefixed services
  // (the only condition under which the V/P modal / inline change link is meaningful).
  function isAnatomyCategory(zenotiCat) {
    if (!zenotiCat) return false;
    return categoryNeedsAnatomy(allCenterServices?.services?.[zenotiCat.id]);
  }

  // Restore selectedCategory from the URL on direct loads of /services/:id (refresh / shared link).
  useEffect(() => {
    if (step !== 4 || selectedCategory || categories.length === 0) return;
    const path = window.location.pathname;
    if (!path.startsWith('/services/')) return;
    const idFromUrl = path.slice('/services/'.length);
    const matched = categories.find(c => String(c.id) === idFromUrl);
    if (matched) handleCategorySelect(matched);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, categories]);

  // Sort services: Nails Feet before Nails Hands (pedicure first), C- classic polish always last
  function sortServiceOrder(services) {
    return [...services].sort((a, b) => {
      const aIsClassic = (a.code || '').startsWith('C-');
      const bIsClassic = (b.code || '').startsWith('C-');
      // Classic polish always last
      if (aIsClassic && !bIsClassic) return 1;
      if (!aIsClassic && bIsClassic) return -1;
      // Pedicure (Nails Feet) before manicure (Nails Hands)
      const aCat = getCategoryForService(a.id).toLowerCase();
      const bCat = getCategoryForService(b.id).toLowerCase();
      const aIsFeet = aCat.includes('nails feet');
      const bIsFeet = bCat.includes('nails feet');
      const aIsHands = aCat.includes('nails hands');
      const bIsHands = bCat.includes('nails hands');
      if (aIsFeet && bIsHands) return -1;
      if (aIsHands && bIsFeet) return 1;
      return 0;
    });
  }

  function isSprayTan(svc) {
    return getCategoryForService(svc.id).toLowerCase().includes('spray tan');
  }

  function isLashOrBrow(svc) {
    const name = (svc.display_name || svc.name || '').toLowerCase();
    return name.includes('lash lift') || name.includes('brow lam');
  }

  // Toggle service in/out of selectedServices (kiosk pattern)
  function toggleService(svc) {
    // Check for spray tan + lash lift/brow lamination conflict
    const isRemoving = selectedServices.some(s => s.id === svc.id);
    if (!isRemoving) {
      const addingSprayTan = isSprayTan(svc);
      const addingLashBrow = isLashOrBrow(svc);
      if (addingSprayTan && selectedServices.some(s => isLashOrBrow(s))) {
        setConflictWarning('Spray tan cannot be booked on the same day as a lash lift or brow lamination service.');
        return;
      }
      if (addingLashBrow && selectedServices.some(s => isSprayTan(s))) {
        setConflictWarning('Lash lift and brow lamination services cannot be booked on the same day as a spray tan.');
        return;
      }
    }
    setSelectedServices(prev => {
      const exists = prev.find(s => s.id === svc.id);
      if (exists) return prev.filter(s => s.id !== svc.id);
      return sortServiceOrder([...prev, svc]);
    });
  }

  // Look up the top-level category name for a service (e.g., "Waxing", "Nails Hands")
  function getCategoryForService(serviceId) {
    if (!allCenterServices?.services) return '';
    const svcs = allCenterServices.services;
    for (const catId of Object.keys(svcs)) {
      const subcats = svcs[catId];
      if (!subcats || typeof subcats !== 'object') continue;
      // Handle flat list format: { catId: [services] }
      if (Array.isArray(subcats)) {
        if (subcats.some(s => s.id === serviceId)) {
          const cat = categories.find(c => c.id === catId);
          return cat?.name || '';
        }
        continue;
      }
      // Handle nested format: { catId: { subCatId: [services] } }
      for (const subId of Object.keys(subcats)) {
        const list = subcats[subId];
        if (Array.isArray(list) && list.some(s => s.id === serviceId)) {
          const cat = categories.find(c => c.id === catId);
          return cat?.name || '';
        }
      }
    }
    return '';
  }

  // Determine if a mandatory service should use the waxing flow (skip Step 6)
  function isWaxingService(svc) {
    return getCategoryForService(svc.id).toLowerCase().includes('wax');
  }

  // Determine if a service is a nail service (triggers guided add-on flow)
  function isNailService(svc) {
    return getCategoryForService(svc.id).toLowerCase().includes('nail');
  }

  // Get the right starting step for a mandatory service
  function getMandatoryStartStep(svc) {
    return isWaxingService(svc) ? 7 : 6;
  }

  // Rebook handler — pre-fills center, service, therapist and drops into the normal flow
  async function handleRebook(appointment) {
    // 1. Find the center
    const center = centers.find(c => c.name === appointment.center?.name) || centers.find(c => c.id === appointment.center?.id);
    if (center) {
      setSelectedCenter(center);
    }

    // 2. Get the catalog for this center (should be pre-cached)
    const centerId = center?.id || selectedCenter?.id;
    const catalog = centerId ? (catalogCache[centerId] || await fetchFullCatalog(centerId)) : null;

    // 3. Find the matching service in the catalog
    const apptServiceId = appointment.service?.id || appointment.services?.[0]?.id;
    const apptServiceName = appointment.service?.name || appointment.services?.[0]?.name;
    let matchedService = null;

    if (catalog?.services) {
      for (const catServices of Object.values(catalog.services)) {
        const match = catServices.find(s =>
          (apptServiceId && s.id === apptServiceId) ||
          (apptServiceName && (s.name === apptServiceName || s.display_name === apptServiceName))
        );
        if (match) { matchedService = match; break; }
      }
    }

    // 4. Build the service object
    const service = matchedService || {
      id: apptServiceId,
      name: apptServiceName,
      display_name: apptServiceName,
      duration: appointment.service?.duration || appointment.services?.[0]?.duration || 0,
      price_info: { sale_price: 0 },
    };

    // 5. Pre-select therapist if available, with price from catalog cache
    const therapistName = appointment.therapist?.name || appointment.services?.[0]?.therapistName;
    const therapistId = appointment.therapist?.id || appointment.services?.[0]?.therapistId;
    if (therapistId || therapistName) {
      // Look up therapist price from cached catalog data
      let therapistPrice = null;
      const cachedTherapists = catalog?.therapists?.[service.id] || [];
      const cachedMatch = cachedTherapists.find(t => t.Id === therapistId || t.id === therapistId);
      if (cachedMatch?.Price != null) {
        therapistPrice = cachedMatch.Price;
      }
      setSelectedTherapists({ [service.id]: { Id: therapistId || 'any', DisplayName: therapistName || 'No Preference', Price: therapistPrice } });
    }

    // 6. Reset cart and add-ons, then set the service and go to add-ons flow
    setServiceCart([]);
    setSelectedOptionalAddOns([]);
    setSelectedMandatoryAddOns([]);
    setSelectedServices([service]);
    setSelectedTime(null); // Clear — user will pick time in normal flow
    setPendingRebookAddOns(true);
  }

  // After rebook sets selectedServices, trigger add-on flow
  useEffect(() => {
    if (pendingRebookAddOns && selectedServices.length > 0) {
      setPendingRebookAddOns(false);
      proceedFromServices();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRebookAddOns, selectedServices]);

  // Called when user clicks "Next" after toggling services — reads add-ons from cached catalog
  function proceedFromServices() {
    setProceedingFromServices(true);
    setNeedsRemoval(null);
    setCurrentMandatoryIdx(0);

    // Figure out which services already had add-ons selected (from a previous pass)
    const existingServiceIds = new Set(serviceCart.map(item => item.service.id));

    // Preserve add-on selections for services that are still selected
    setSelectedOptionalAddOns(prev => prev.filter(a => selectedServices.some(s => s.id === a.parentServiceId)));
    setSelectedMandatoryAddOns(prev => prev.filter(a => selectedServices.some(s => s.id === a.parentServiceId)));

    // Read add-ons from cached full catalog (instant — no API calls)
    // For waxing services, move "Hard Wax" subcategory add-ons to mandatory
    // so they appear in the big-button "Choose Your Wax" step
    const addOns = {};
    // Filter out internal-only add-ons (Z SQZIN, Z Natural, etc.)
    const filterInternal = (list) => (list || []).filter(a => !/^Z\s/i.test(a.name || ''));
    selectedServices.forEach(svc => {
      if (allCenterServices?.addOns?.[svc.id]) {
        const raw = allCenterServices.addOns[svc.id];
        // Pass-through optional/mandatory from the catalog. Guided handles wax-type
        // (hard wax) directly from the optional list — no need to move it to
        // mandatory the way the legacy booking flow did.
        addOns[svc.id] = {
          optional: filterInternal(raw.optional),
          mandatory: filterInternal(raw.mandatory),
        };
      } else {
        addOns[svc.id] = { optional: [], mandatory: [] };
      }
    });
    setServiceAddOns(addOns);

    // Identify services with mandatory add-ons (legacy fallback for RemovalDecisionStep / RemovalTypeStep).
    const withMandatory = selectedServices.filter(svc => (addOns[svc.id]?.mandatory || []).length > 0);
    setMandatoryServices(withMandatory);

    // Determine which services need add-on selection this pass
    const newServices = serviceCart.length === 0
      ? selectedServices
      : selectedServices.filter(s => !existingServiceIds.has(s.id));

    // All new services (including waxing) flow through Guided — Guided's wax-type
    // screen handles hard wax, its removal screen handles nail polish removal,
    // its pampering screen handles everything else, and it skips empty sections.
    setAddOnServices(newServices);
    setUseGuidedFlow(true);

    // Skip step 5 entirely if NO service has any add-on (mandatory or optional).
    const hasAnyAddOns = newServices.some(svc =>
      (addOns[svc.id]?.optional || []).length > 0 || (addOns[svc.id]?.mandatory || []).length > 0
    );

    const newMandatory = serviceCart.length === 0
      ? withMandatory
      : withMandatory.filter(svc => !existingServiceIds.has(svc.id));

    if (hasAnyAddOns) {
      setStep(5);
    } else if (newMandatory.length > 0) {
      setMandatoryServices(newMandatory);
      setSelectedService(newMandatory[0]);
      setStep(getMandatoryStartStep(newMandatory[0]));
    } else {
      setStep(8);
    }
    setProceedingFromServices(false);
  }

  function toggleOptionalAddOn(addOn, parentServiceId) {
    setSelectedOptionalAddOns(prev => {
      const exists = prev.find(a => a.id === addOn.id && a.parentServiceId === parentServiceId);
      if (exists) return prev.filter(a => !(a.id === addOn.id && a.parentServiceId === parentServiceId));
      return [...prev, { ...addOn, parentServiceId }];
    });
  }

  function handleGuidedFlowComplete() {
    // Advance to mandatory add-ons or therapist
    if (mandatoryServices.length > 0) {
      setCurrentMandatoryIdx(0);
      setSelectedService(mandatoryServices[0]);
      setTimeout(() => setStep(getMandatoryStartStep(mandatoryServices[0])), 300);
    } else {
      setTimeout(() => setStep(8), 300);
    }
  }

  function handleRemovalDecision(decision) {
    setNeedsRemoval(decision);
    if (decision === 'yes') {
      setTimeout(() => setStep(7), 300); // Select which removal type
    } else {
      // Advance to next mandatory service or to therapist
      advanceMandatory();
    }
  }

  function toggleMandatoryAddOn(addOn, parentServiceId) {
    setSelectedMandatoryAddOns(prev => {
      const exists = prev.find(a => a.id === addOn.id && a.parentServiceId === parentServiceId);
      if (exists) return prev.filter(a => !(a.id === addOn.id && a.parentServiceId === parentServiceId));
      return [...prev, { ...addOn, parentServiceId }];
    });
  }

  function handleMandatoryAddOnsContinue() {
    advanceMandatory();
  }

  // Move to next mandatory service or proceed to therapist step
  function advanceMandatory() {
    const nextIdx = currentMandatoryIdx + 1;
    if (nextIdx < mandatoryServices.length) {
      setCurrentMandatoryIdx(nextIdx);
      setSelectedService(mandatoryServices[nextIdx]);
      setNeedsRemoval(null);
      setTimeout(() => setStep(getMandatoryStartStep(mandatoryServices[nextIdx])), 300);
    } else {
      setTimeout(() => setStep(8), 300);
    }
  }

  // Build final serviceCart from gathered selections and proceed
  function buildCartAndContinue() {
    const cart = sortServiceOrder(selectedServices).map(svc => ({
      service: svc,
      optionalAddOns: selectedOptionalAddOns.filter(a => a.parentServiceId === svc.id),
      mandatoryAddOns: selectedMandatoryAddOns.filter(a => a.parentServiceId === svc.id),
      therapist: selectedTherapists[svc.id] || { Id: 'any', DisplayName: 'No Preference' }
    }));
    setServiceCart(cart);
    setStep(8.5);
  }

  // When adding another service, preserve add-on selections from existing cart
  function handleAddAnotherService() {
    setSelectedService(null);
    // Restore add-on selections from cart items so they're preserved
    const restoredOptional = [];
    const restoredMandatory = [];
    serviceCart.forEach(item => {
      (item.optionalAddOns || []).forEach(a => restoredOptional.push(a));
      (item.mandatoryAddOns || []).forEach(a => restoredMandatory.push(a));
    });
    setSelectedOptionalAddOns(restoredOptional);
    setSelectedMandatoryAddOns(restoredMandatory);
    setNeedsRemoval(null);
    setSelectedTherapist(null);
    setStep(4);
  }

  function handleRemoveCartItem(index) {
    const removedItem = serviceCart[index];
    const updated = serviceCart.filter((_, i) => i !== index);
    setServiceCart(updated);
    // Also remove from selectedServices
    if (removedItem) {
      setSelectedServices(prev => prev.filter(s => s.id !== removedItem.service.id));
    }
    // If cart is empty, go back to service selection
    if (updated.length === 0) {
      setStep(4);
    }
  }

  function handleContinueToTime() {
    // Save cart to guest selections
    const newSelections = [...guestSelections];
    newSelections[currentGuestIndex] = {
      ...newSelections[currentGuestIndex],
      services: serviceCart,
      anatomyFilter: anatomyFilter,
      therapist: null
    };
    setGuestSelections(newSelections);
    
    // For multi-guest, check if all guests have services
    if (guestCount > 1) {
      const allComplete = newSelections.every(g => g.services && g.services.length > 0);
      setStep(allComplete ? 9 : 3);
    } else {
      setStep(9);
    }
  }

  function skipTherapist() {
    // Default all services to "No Preference" and build cart
    const defaults = {};
    selectedServices.forEach(svc => { defaults[svc.id] = { Id: 'any', DisplayName: 'No Preference' }; });
    setSelectedTherapists(defaults);
    buildCartAndContinue();
  }

  // =====================
  // TIME SELECTION HELPERS
  // =====================

  function getDayAvailability(date) {
    const dayStr = date.toISOString().split('T')[0];
    const dayInfo = futureDays.find(fd => fd.Day && fd.Day.startsWith(dayStr));
    if (dayInfo) return dayInfo.IsAvailable;
    // If it's the selected date and we have slots, it's available
    if (selectedDate && isSameDay(date, selectedDate) && availableSlots.length > 0) return true;
    return null; // unknown
  }

  // Snap a date back to its Sunday
  function getSunday(date) {
    const d = new Date(date);
    d.setDate(d.getDate() - d.getDay());
    d.setHours(0, 0, 0, 0);
    return d;
  }

  function initTimeSelection() {
    const today = new Date();
    setWeekStart(getSunday(today));
    setSelectedDate(today);
    setSelectedTime(null);
    setAvailableSlots([]);
    setFutureDays([]);
    // Load a mid-week date first to get FutureDays, then auto-select best day
    autoSelectBestDay(today, true);
  }

  async function autoSelectBestDay(weekStartDate, showModal = false, weeksSearched = 0) {
    setLoadingSlots(true);
    try {
      const services = getServicesForAvailability();
      const queryDate = new Date(weekStartDate);
      const midWeekStr = `${queryDate.getFullYear()}-${String(queryDate.getMonth() + 1).padStart(2, '0')}-${String(queryDate.getDate()).padStart(2, '0')}`;

      const response = await fetch(`${API_URL}/api/availability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          centerId: selectedCenter.id,
          date: midWeekStr,
          services,
          checkFutureDays: true
        })
      });
      const data = await response.json();

      if (data.futureDays && data.futureDays.length > 0) {
        setFutureDays(prev => {
          const existing = new Map(prev.map(d => [d.Day?.split('T')[0], d]));
          data.futureDays.forEach(d => {
            const key = d.Day?.split('T')[0];
            if (key) existing.set(key, d);
          });
          return Array.from(existing.values());
        });

        // Find first available day from ALL returned future days (not just current week)
        const availableDays = data.futureDays
          .filter(d => d.IsAvailable)
          .map(d => d.Day?.split('T')[0])
          .sort();

        if (availableDays.length > 0) {
          const firstAvailStr = availableDays[0];
          const [y, m, d] = firstAvailStr.split('-').map(Number);
          const firstAvail = new Date(y, m - 1, d);

          setSelectedDate(firstAvail);
          setWeekStart(getSunday(firstAvail));
          if (showModal && !isToday(firstAvail)) {
            setAutoAdvanceDate(firstAvail);
            setShowAutoAdvanceToast(true);
          }
          await loadAvailableSlots(firstAvail, false, true);
          // Load future days for the rest of the week to show green dots
          const midWeek = getSunday(firstAvail);
          midWeek.setDate(midWeek.getDate() + 3);
          loadFutureDaysOnly(midWeek);
          return;
        }
      }

      // No available days found — look ahead to the next week (up to 8 weeks out)
      if (weeksSearched < 8) {
        const nextWeek = new Date(weekStartDate);
        nextWeek.setDate(nextWeek.getDate() + 7);
        return autoSelectBestDay(nextWeek, showModal, weeksSearched + 1);
      }

      // Exhausted search — just stop loading
      setLoadingSlots(false);
    } catch (err) {
      console.error('Failed to auto-select day:', err);
      setLoadingSlots(false);
    }
  }

  async function loadFutureDaysOnly(date) {
    try {
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      const services = getServicesForAvailability();
      const response = await fetch(`${API_URL}/api/availability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          centerId: selectedCenter.id,
          date: dateStr,
          services,
          checkFutureDays: true
        })
      });
      const data = await response.json();
      if (data.futureDays && data.futureDays.length > 0) {
        setFutureDays(prev => {
          const existing = new Map(prev.map(d => [d.Day?.split('T')[0], d]));
          data.futureDays.forEach(d => {
            const key = d.Day?.split('T')[0];
            if (key) existing.set(key, d);
          });
          return Array.from(existing.values());
        });
      }
    } catch (err) {
      console.error('Failed to load future days:', err);
    }
  }



  // If a service has been relaxed for slot search, send "any" instead of the user's preferred therapist.
  function getEffectiveTherapistId(item) {
    if (relaxedServices.includes(item.service.id)) return 'any';
    return (item.therapist?.Id || item.therapist?.id) || 'any';
  }

  function getBookingServices() {
    // Collect all services from all guests for the availability + booking requests
    const services = [];
    guestSelections.forEach(guest => {
      if (guest.services) {
        guest.services.forEach(item => {
          const therapistId = getEffectiveTherapistId(item);

          // Main service
          services.push({
            serviceId: item.service.id,
            serviceName: item.service.display_name || item.service.name,
            duration: item.service.duration,
            therapistId,
            isAddOn: false,
            parentServiceId: null,
            categoryId: item.service.category_id || ''
          });
          
          // Optional add-ons
          (item.optionalAddOns || []).forEach(addOn => {
            services.push({
              serviceId: addOn.id,
              serviceName: addOn.name,
              duration: addOn.duration || 0,
              therapistId,
              isAddOn: true,
              isMandatory: false,
              parentServiceId: item.service.id,
              price: addOn.price || 0,
              tax: addOn.tax || 0,
              code: addOn.code || '',
              categoryId: addOn.category_id || '',
              parentCategoryId: addOn.parent_category_id || '',
              categoryName: addOn.category_name || '',
              subCategoryName: addOn.sub_category_name || ''
            });
          });
          
          // Mandatory add-ons
          (item.mandatoryAddOns || []).forEach(addOn => {
            services.push({
              serviceId: addOn.id,
              serviceName: addOn.name,
              duration: addOn.duration || 0,
              therapistId,
              isAddOn: true,
              isMandatory: true,
              parentServiceId: item.service.id,
              price: addOn.price || 0,
              tax: addOn.tax || 0,
              code: addOn.code || '',
              categoryId: addOn.category_id || '',
              parentCategoryId: addOn.parent_category_id || '',
              categoryName: addOn.category_name || '',
              subCategoryName: addOn.sub_category_name || ''
            });
          });
        });
      }
    });
    return services;
  }

  // Returns services in grouped format for multi-guest bookings so availability
  // checks find times where all guests can be served simultaneously
  function getServicesForAvailability() {
    const flat = getBookingServices();
    if (guestCount <= 1) return flat;
    const guestGroups = guestSelections.map(guest => {
      const guestServices = [];
      if (guest.services) {
        guest.services.forEach(item => {
          const therapistId = getEffectiveTherapistId(item);
          guestServices.push({
            serviceId: item.service.id,
            serviceName: item.service.display_name || item.service.name,
            duration: item.service.duration,
            therapistId,
            isAddOn: false,
            parentServiceId: null,
            categoryId: item.service.category_id || ''
          });
          (item.optionalAddOns || []).forEach(addOn => {
            guestServices.push({
              serviceId: addOn.id, serviceName: addOn.name, duration: addOn.duration || 0,
              therapistId, isAddOn: true, isMandatory: false, parentServiceId: item.service.id
            });
          });
          (item.mandatoryAddOns || []).forEach(addOn => {
            guestServices.push({
              serviceId: addOn.id, serviceName: addOn.name, duration: addOn.duration || 0,
              therapistId, isAddOn: true, isMandatory: true, parentServiceId: item.service.id
            });
          });
        });
      }
      return { services: guestServices };
    });
    return { guestGroups, flat };
  }

  async function loadAvailableSlots(date, checkFutureDays = true, isAutoAdvance = false) {
    setLoadingSlots(true);
    try {
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      const services = getServicesForAvailability();
      
      const response = await fetch(`${API_URL}/api/availability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          centerId: selectedCenter.id,
          date: dateStr,
          services,
          checkFutureDays
        })
      });
      const data = await response.json();
      
      setAvailableSlots(data.openSlots || []);
      if (data.futureDays && data.futureDays.length > 0) {
        setFutureDays(prev => {
          const existing = new Map(prev.map(d => [d.Day?.split('T')[0], d]));
          data.futureDays.forEach(d => {
            const key = d.Day?.split('T')[0];
            if (key) existing.set(key, d);
          });
          return Array.from(existing.values());
        });
      }
      
      // If no slots on initial load, auto-advance to next available day
      if (!isAutoAdvance && (!data.openSlots || data.openSlots.length === 0)) {
        // Try Zenoti's NextAvailableDay first, then check FutureDays
        let nextDayStr = data.nextAvailableDay;
        if (!nextDayStr && data.futureDays?.length > 0) {
          nextDayStr = data.futureDays
            .filter(d => d.IsAvailable)
            .map(d => d.Day?.split('T')[0])
            .sort()[0];
        }
        if (nextDayStr) {
          const [y, m, d] = nextDayStr.split('-').map(Number);
          const nextDay = new Date(y, m - 1, d);
          setSelectedDate(nextDay);
          if (nextDay > getWeekDays(weekStart)[6]) {
            setWeekStart(getSunday(nextDay));
          }
          loadAvailableSlots(nextDay, true, true);
          return;
        }
      }
    } catch (err) {
      console.error('Failed to load availability:', err);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }

  function handleDateSelect(date) {
    if (isPast(date) && !isToday(date)) return;
    const avail = getDayAvailability(date);
    if (avail === false) return;
    setSelectedDate(date);
    setSelectedTime(null);
    loadAvailableSlots(date, true);
  }

  // Services where the user picked a specific provider (not "any") and that aren't already relaxed.
  function getPreferencedServices() {
    const result = [];
    const seen = new Set();
    guestSelections.forEach(guest => {
      (guest.services || []).forEach(item => {
        const tId = item.therapist?.Id || item.therapist?.id;
        if (tId && tId !== 'any' && !relaxedServices.includes(item.service.id) && !seen.has(item.service.id)) {
          seen.add(item.service.id);
          result.push({ id: item.service.id, name: item.service.display_name || item.service.name });
        }
      });
    });
    return result;
  }

  function getRelaxedServiceLabels() {
    const map = new Map();
    guestSelections.forEach(guest => {
      (guest.services || []).forEach(item => {
        if (relaxedServices.includes(item.service.id)) {
          map.set(item.service.id, item.service.display_name || item.service.name);
        }
      });
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }

  function handleRelaxService(serviceId) {
    setRelaxedServices(prev => prev.includes(serviceId) ? prev : [...prev, serviceId]);
  }

  function handleRelaxAllServices() {
    const allIds = [];
    guestSelections.forEach(guest => {
      (guest.services || []).forEach(item => {
        const tId = item.therapist?.Id || item.therapist?.id;
        if (tId && tId !== 'any') allIds.push(item.service.id);
      });
    });
    setRelaxedServices(prev => Array.from(new Set([...prev, ...allIds])));
  }

  function handleResetRelaxed() {
    setRelaxedServices([]);
  }

  // Clear relaxed preferences whenever the user returns to the provider step.
  useEffect(() => {
    if (step === 8 && relaxedServices.length > 0) setRelaxedServices([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Re-fetch slots when relaxed services change (skip first mount).
  useEffect(() => {
    if (relaxedFirstRunRef.current) { relaxedFirstRunRef.current = false; return; }
    if (step === 9 && selectedDate) {
      loadAvailableSlots(selectedDate, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relaxedServices]);

  function handleWeekNav(direction) {
    const newStart = new Date(weekStart);
    newStart.setDate(newStart.getDate() + (direction * 7));
    const thisSunday = getSunday(new Date());
    if (newStart < thisSunday) return;
    setWeekStart(newStart);
    setSelectedTime(null);
    setAvailableSlots([]);
    autoSelectBestDay(newStart);
  }

  function handleTimeSelect(slot) {
    setSelectedTime(slot.Time);
  }

  // =====================
  // RECAPTCHA
  // =====================
  function loadRecaptchaScript() {
    return new Promise((resolve) => {
      if (window.grecaptcha) { resolve(); return; }
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
      script.onload = () => {
        window.grecaptcha.ready(() => resolve());
      };
      document.head.appendChild(script);
    });
  }

  async function getRecaptchaToken() {
    await loadRecaptchaScript();
    return new Promise((resolve) => {
      window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'send_otp' }).then(resolve);
    });
  }

  // =====================
  // AUTH HANDLERS
  // =====================
  async function handlePhoneSubmit(intent) {
    // intent: 'otp' or 'password'
    // Accept either phone or email
    const input = authPhone.trim();
    const digits = input.replace(/\D/g, '');
    const isPhone = digits.length >= 10 && /^\(?\d/.test(input);
    const isEmail = input.includes('@');

    // OTP is SMS-only — force password for email
    const chosenIntent = isEmail ? 'password' : (intent || 'otp');
    setAuthIntent(chosenIntent);

    if (!isPhone && !isEmail) {
      setAuthError('Please enter a valid phone number or email address.');
      return;
    }
    setAuthLoading(true);
    setAuthError('');
    try {
      const checkBody = isPhone ? { phone: digits } : { email: input };
      const checkRes = await fetch(`${API_URL}/api/auth/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(checkBody)
      });
      const checkData = await checkRes.json();

      if (checkData.exists) {
        // Save the actual username Zenoti wants for login
        const resolvedUserName = checkData.loginUserName || (isPhone ? digits : input);
        setAuthLoginUserName(resolvedUserName);

        if (checkData.multipleMatches && isPhone) {
          // Multiple accounts share this phone — ask for first name to disambiguate
          setAuthMode('multipleAccounts');
        } else if (chosenIntent === 'otp') {
          // Single match + OTP intent → send code immediately
          await sendOtpForUser(resolvedUserName);
        } else {
          setAuthMode('password');
        }
      } else {
        // New user — go to signup form
        setAuthMode('signup');
      }
    } catch (err) {
      setAuthError('Something went wrong. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  }

  async function handleMultipleAccountsSubmit() {
    if (!authFirstName.trim()) {
      setAuthError('Please enter your first name.');
      return;
    }
    setAuthLoading(true);
    setAuthError('');
    try {
      const digits = authPhone.replace(/\D/g, '');
      const res = await fetch(`${API_URL}/api/auth/disambiguate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: digits,
          firstName: authFirstName.trim()
        })
      });
      const data = await res.json();
      
      if (data.found && data.loginUserName) {
        // Found the account — save the username and proceed based on intent
        setAuthLoginUserName(data.loginUserName);
        if (authIntent === 'otp') {
          // Send OTP immediately after disambiguation
          await sendOtpForUser(data.loginUserName);
        } else {
          setAuthMode('password');
        }
      } else {
        setAuthError(data.error || 'Could not find your account. Please try your email address instead.');
      }
    } catch (err) {
      setAuthError('Something went wrong. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  }

  async function sendOtpForUser(userName) {
    // Core OTP send logic — called from phone submit, disambiguation, and resend
    // OTP is SMS-only, so if userName is an email, use the original phone number instead
    const otpTarget = userName.includes('@') ? authPhone.replace(/\D/g, '') : userName;
    try {
      const res = await fetch(`${API_URL}/api/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userName: otpTarget, centerId: selectedCenter?.id })
      });
      const data = await res.json();
      if (data.success && data.verificationId) {
        setAuthVerificationId(data.verificationId);
        setAuthUserName(userName);
        setAuthOtp('');
        setAuthMode('otp');
      } else {
        setAuthError(data.error || 'Could not send verification code. Please try password instead.');
      }
    } catch (err) {
      setAuthError('Something went wrong. Please try again.');
    }
  }

  async function handleSendOtp() {
    const userName = authLoginUserName || authPhone.trim();
    if (!userName) {
      setAuthError('Please enter your phone or email first.');
      return;
    }
    setAuthLoading(true);
    setAuthError('');
    await sendOtpForUser(userName);
    setAuthLoading(false);
  }

  async function handleOtpSubmit() {
    if (!authOtp || authOtp.length < 4) {
      setAuthError('Please enter the verification code.');
      return;
    }
    setAuthLoading(true);
    setAuthError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userName: authUserName,
          otp: authOtp,
          verificationId: authVerificationId
        })
      });
      const data = await res.json();
      if (data.success) {
        setGuestInfo({
          guestId: data.guestId,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          webApiToken: data.webApiToken
        });
        if (step === 13 || step === 14) {
          // GiftCardPage / PackagesPage handle navigation via useEffect on guestInfo
        } else if (signInFromNav) {
          setSignInFromNav(false);
          setShowSignInModal(false);
          signInModalRef.current = false;
          setStep(1);
          setShowQuickRebookModal(true);
          loadAccountData(data.guestId);
        } else if (step === 12) {
          setStep(1);
          setShowQuickRebookModal(true);
          loadAccountData(data.guestId);
        } else {
          setStep(10);
          loadAccountData(data.guestId);
        }
      } else {
        setAuthError('Invalid code. Please try again.');
      }
    } catch (err) {
      setAuthError('Something went wrong. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  }

  async function handlePasswordSubmit() {
    if (!authPhone || !authPassword) {
      setAuthError('Please enter your email/phone and password.');
      return;
    }
    setAuthLoading(true);
    setAuthError('');
    try {
      // Determine the userName to send to Zenoti
      let userName = authLoginUserName;
      
      if (!userName) {
        // User changed the field — re-check with Zenoti to get actualUserName
        const input = authPhone.trim();
        const digits = input.replace(/\D/g, '');
        const isPhone = digits.length >= 10 && /^\(?\d/.test(input);
        
        const checkBody = isPhone ? { phone: digits } : { email: input };
        const checkRes = await fetch(`${API_URL}/api/auth/check`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(checkBody)
        });
        const checkData = await checkRes.json();
        
        if (checkData.exists && checkData.loginUserName) {
          userName = checkData.loginUserName;
          setAuthLoginUserName(userName);
        } else if (checkData.multipleMatches && isPhone) {
          setAuthError('Multiple accounts found with this phone number. Please use your email address instead.');
          setAuthLoading(false);
          return;
        } else {
          // Fall back to what user typed
          userName = isPhone ? digits : input;
        }
      }
      
      const res = await fetch(`${API_URL}/api/auth/password-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userName: userName, password: authPassword })
      });
      const data = await res.json();
      if (data.success) {
        setGuestInfo({
          guestId: data.guestId,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          webApiToken: data.webApiToken
        });
        if (step === 13 || step === 14) {
          // GiftCardPage / PackagesPage handle navigation via useEffect on guestInfo
        } else if (signInFromNav) {
          setSignInFromNav(false);
          setShowSignInModal(false);
          signInModalRef.current = false;
          setStep(1);
          setShowQuickRebookModal(true);
          loadAccountData(data.guestId);
        } else if (step === 12) {
          setStep(1);
          setShowQuickRebookModal(true);
          loadAccountData(data.guestId);
        } else {
          setStep(10);
          loadAccountData(data.guestId);
        }
      } else {
        setAuthError(data.error || 'Incorrect password. Please try again.');
      }
    } catch (err) {
      setAuthError('Something went wrong. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  }

  async function loadAccountData(guestIdOverride, full = false) {
    const id = guestIdOverride || guestInfo?.guestId;
    if (!id) return;
    setAccountLoading(true);
    try {
      if (full) {
        // Full summary for account page — gift cards, packages, memberships, loyalty
        const cId = selectedCenter?.id || '';
        const res = await fetch(`${API_URL}/api/account/summary/${id}?centerId=${cId}`);
        const data = await res.json();
        setAccountData(prev => ({ ...prev, ...data }));
      } else {
        // Lightweight — recent visits + upcoming for quick rebook modal (2 parallel calls)
        const [historyRes, upcomingRes] = await Promise.all([
          fetch(`${API_URL}/api/account/appointment-history/${id}`),
          fetch(`${API_URL}/api/account/appointments/${id}`)
        ]);
        const historyData = await historyRes.json();
        const upcomingData = await upcomingRes.json();
        const visits = (historyData.appointments || []).filter(a => a.status === 2 || a.status === undefined).slice(0, 15);
        const upcoming = upcomingData.upcoming || upcomingData.appointments || [];
        setAccountData(prev => ({ ...prev, recentVisits: visits, upcoming }));
      }
    } catch (err) {
      console.error('Failed to load account data:', err);
    } finally {
      setAccountLoading(false);
    }
  }

  async function handleForgotPassword() {
    const userName = authLoginUserName || authPhone.trim();
    if (!userName) {
      setAuthError('Please enter your email or phone first.');
      return;
    }
    setAuthLoading(true);
    setAuthError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userName })
      });
      const data = await res.json();
      if (data.success) {
        setResetUserId(data.userId || '');
        setResetVerificationId(data.verificationId || '');
        setResetCode('');
        setResetNewPassword('');
        setResetConfirmPassword('');
        setResetSuccess(false);
        setAuthError('');
        setAuthMode('forgotSent');
      } else {
        setAuthError(data.error || 'Could not send reset email. Please try again.');
      }
    } catch (err) {
      setAuthError('Something went wrong. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  }

  async function handleResetPassword() {
    if (!resetCode) { setAuthError('Please enter the code from your email.'); return; }
    if (!resetNewPassword || resetNewPassword.length < 8) { setAuthError('Password must be at least 8 characters.'); return; }
    if (resetNewPassword !== resetConfirmPassword) { setAuthError('Passwords do not match.'); return; }
    if (!/[a-z]/.test(resetNewPassword) || !/[A-Z]/.test(resetNewPassword) || !/[0-9]/.test(resetNewPassword) || !/[~!@#$%^&*()_+=]/.test(resetNewPassword)) {
      setAuthError('Password needs lowercase, uppercase, number, and special character.'); return;
    }
    if (!resetUserId) { setAuthError('Something went wrong. Please try resetting again.'); return; }
    setAuthLoading(true); setAuthError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: resetUserId, verificationId: resetVerificationId, code: resetCode, newPassword: resetNewPassword })
      });
      const data = await res.json();
      if (data.success) {
        setResetSuccess(true);
        setAuthError('');
      } else { setAuthError(data.error || 'Could not reset password. Please check your code and try again.'); }
    } catch (err) { setAuthError('Something went wrong. Please try again.'); }
    finally { setAuthLoading(false); }
  }

  async function handleSignupSubmit() {
    if (!authFirstName || !authLastName || !authEmail || !authDob) {
      setAuthError('Please fill in all required fields.');
      return;
    }
    if (!authPassword || authPassword.length < 8) {
      setAuthError('Password must be at least 8 characters.');
      return;
    }
    if (authPassword !== authConfirmPassword) {
      setAuthError('Passwords do not match.');
      return;
    }
    if (!/[a-z]/.test(authPassword) || !/[A-Z]/.test(authPassword) || !/[0-9]/.test(authPassword) || !/[~!@#$%^&*()_+=]/.test(authPassword)) {
      setAuthError('Password needs lowercase, uppercase, number, and special character.');
      return;
    }
    const digits = authPhone.replace(/\D/g, '');
    setAuthLoading(true);
    setAuthError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/create-guest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: authFirstName,
          lastName: authLastName,
          phone: digits,
          email: authEmail,
          dateOfBirth: authDob,
          gender: authGender ? parseInt(authGender) : -1,
          referralCode: authReferralCode,
          howHeard: authHowHeard,
          password: authPassword,
          marketingEmail: authMarketingEmail,
          marketingSms: authMarketingSms,
          centerId: selectedCenter.id
        })
      });
      const data = await res.json();
      if (data.success) {
        setGuestInfo({
          guestId: data.guestId,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          webApiToken: data.webApiToken
        });
        if (step === 13 || step === 14) {
          // GiftCardPage / PackagesPage handle navigation via useEffect on guestInfo
        } else if (signInFromNav) {
          setSignInFromNav(false);
          setShowSignInModal(false);
          signInModalRef.current = false;
          setStep(1);
          setShowQuickRebookModal(true);
          loadAccountData(data.guestId);
        } else {
          setStep(10);
        }
      } else {
        setAuthError(data.error || 'Failed to create account. Please try again.');
      }
    } catch (err) {
      setAuthError('Something went wrong. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  }

  // If this is a popup returning from gift card payment, close it
  useEffect(() => {
    if (isGcPaidPopup) { window.close(); }
  }, [isGcPaidPopup]);

  // If this is a popup returning from package payment, close it
  useEffect(() => {
    if (isPkgPaidPopup) { window.close(); }
  }, [isPkgPaidPopup]);

  // If navigated back with gc_paid param (non-popup fallback)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('gc_paid') === '1' && !window.opener) {
      window.history.replaceState({ step: 13 }, '', '/gift-cards');
      setStep(13);
    }
  }, []);

  // If navigated back with pkg_paid param (non-popup fallback)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('pkg_paid') === '1' && !window.opener) {
      window.history.replaceState({ step: 14 }, '', '/packages');
      setStep(14);
    }
  }, []);

  // Build the services payload for reserve/confirm
  function buildServicesPayload() {
    const flatServices = getBookingServices();
    if (guestCount > 1) {
      const guestGroups = guestSelections.map((guest, idx) => {
        const guestServices = [];
        if (guest.services) {
          guest.services.forEach(item => {
            const therapistId = getEffectiveTherapistId(item);
            guestServices.push({
              serviceId: item.service.id,
              serviceName: item.service.display_name || item.service.name,
              duration: item.service.duration,
              therapistId,
              isAddOn: false,
              parentServiceId: null,
              categoryId: item.service.category_id || ''
            });
            (item.optionalAddOns || []).forEach(addOn => {
              guestServices.push({
                serviceId: addOn.id, serviceName: addOn.name, duration: addOn.duration || 0,
                therapistId, isAddOn: true, isMandatory: false, parentServiceId: item.service.id,
                price: addOn.price || 0, tax: addOn.tax || 0, code: addOn.code || '',
                categoryId: addOn.category_id || '', parentCategoryId: addOn.parent_category_id || '',
                categoryName: addOn.category_name || '', subCategoryName: addOn.sub_category_name || ''
              });
            });
            (item.mandatoryAddOns || []).forEach(addOn => {
              guestServices.push({
                serviceId: addOn.id, serviceName: addOn.name, duration: addOn.duration || 0,
                therapistId, isAddOn: true, isMandatory: true, parentServiceId: item.service.id,
                price: addOn.price || 0, tax: addOn.tax || 0, code: addOn.code || '',
                categoryId: addOn.category_id || '', parentCategoryId: addOn.parent_category_id || '',
                categoryName: addOn.category_name || '', subCategoryName: addOn.sub_category_name || ''
              });
            });
          });
        }
        return {
          firstName: idx === 0 ? (guestInfo.firstName || '') : (guest.firstName || `Guest ${idx + 1}`),
          lastName: idx === 0 ? (guestInfo.lastName || '') : (guest.lastName || ''),
          email: idx === 0 ? (guestInfo.email || '') : '',
          phone: idx === 0 ? '' : (guest.phone || ''),
          services: guestServices
        };
      });
      return { guestGroups, flat: flatServices };
    }
    return flatServices;
  }

  // Reserve slot when entering step 10 — creates invoice, applies coupons
  async function reserveSlot() {
    if (!guestInfo?.guestId || !selectedTime) return;
    setReserving(true);
    setUpkeepApplied(false);
    setInvoiceTotals(null);
    try {
      const services = buildServicesPayload();
      const reserveRes = await fetch(`${API_URL}/api/booking/reserve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          centerId: selectedCenter.id,
          centerTime: selectedTime,
          guestId: guestInfo.guestId,
          services,
          webApiToken: guestInfo.webApiToken
        })
      });
      const data = await reserveRes.json();

      if (!reserveRes.ok || data.error) {
        const errMsg = typeof data.error === 'string' ? data.error.toLowerCase() : '';
        const isAuthError = reserveRes.status === 401 || reserveRes.status === 403 ||
          errMsg.includes('access') || errMsg.includes('unauthorized') || errMsg.includes('token');
        if (isAuthError) {
          // Expired session — clear guest and prompt re-login
          setGuestInfo(null);
          setError('Your session has expired. Please sign in again.');
          setAuthMode('phone');
          setAuthError('');
          setStep(9.5);
        } else {
          setError(typeof data.error === 'string' ? data.error : 'Failed to reserve time slot. Please try again.');
          setStep(9);
        }
        return;
      }

      setReserveData(data);

      // Auto-apply UPKEEP discount if eligible
      if (upkeepEligibility.length > 0 && data.invoiceId && selectedDate) {
        const selDate = new Date(selectedDate);
        const withinWindow = upkeepEligibility.some(e => selDate <= new Date(e.deadline));
        if (withinWindow) {
          try {
            const couponRes = await fetch(`${API_URL}/api/invoice/apply-coupon`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ invoiceId: data.invoiceId, couponCode: 'UPKEEP', centerId: selectedCenter.id })
            });
            const couponResult = await couponRes.json();
            if (couponResult.success) {
              setUpkeepApplied(true);
              setInvoiceTotals({ subtotal: couponResult.subtotal, discount: couponResult.discount, tax: couponResult.tax, total: couponResult.total });
              console.log('[BOOKING] UPKEEP discount applied, new total:', couponResult.total);
            } else {
              console.log('[BOOKING] UPKEEP apply failed:', couponResult.error);
            }
          } catch (e) {
            console.log('[BOOKING] Could not apply UPKEEP:', e);
          }
        }
      }
    } catch (err) {
      console.error('Reserve error:', err);
      // If it looks like an auth issue, clear session and prompt login
      setError('Something went wrong reserving your slot. Please try again.');
      setStep(9);
    } finally {
      setReserving(false);
    }
  }

  // Apply a manually-entered coupon code to the reserved invoice
  async function applyCouponCode(code) {
    if (!code?.trim() || !reserveData?.invoiceId) return;
    try {
      const res = await fetch(`${API_URL}/api/invoice/apply-coupon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoiceId: reserveData.invoiceId, couponCode: code.trim(), centerId: selectedCenter.id })
      });
      const result = await res.json();
      if (result.success) {
        setInvoiceTotals({ subtotal: result.subtotal, discount: result.discount, tax: result.tax, total: result.total });
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (e) {
      return { success: false, error: 'Something went wrong' };
    }
  }

  async function handleConfirmBooking() {
    if (!guestInfo?.guestId || !reserveData) return;
    setBookingLoading(true);
    try {
      const services = buildServicesPayload();
      const confirmRes = await fetch(`${API_URL}/api/booking/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          centerId: selectedCenter.id,
          invoiceId: reserveData.invoiceId,
          reservationId: reserveData.reservationId,
          guestId: guestInfo.guestId,
          services,
          slotBookings: reserveData.slotBookings,
          webApiToken: guestInfo.webApiToken,
          notes: bookingNotes.trim(),
          v1: reserveData.v1 || false,
          bookingId: reserveData.bookingId || null,
          source
        })
      });
      const confirmData = await confirmRes.json();

      if (confirmData.success) {
        setBookingConfirmed(true);
        setStep(11);

        // Check for unfilled forms in background
        const appointmentGroupId = reserveData.slotBookings?.[0]?.AppointmentGroupId || 
          confirmData.data?.InvoiceDetails?.AppointmentGroupId;
        if (appointmentGroupId && guestInfo?.guestId) {
          try {
            const formsRes = await fetch(`${API_URL}/api/booking/forms?appointmentGroupId=${appointmentGroupId}&guestId=${guestInfo.guestId}`);
            const formsData = await formsRes.json();
            if (formsData.hasUnfilledForms) {
              setUnfilledForms(formsData.forms);
            }
          } catch (e) {
            console.log('Could not check forms:', e);
          }
        }
      } else {
        setError('Failed to confirm booking. Please try again.');
      }
    } catch (err) {
      console.error('Booking error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setBookingLoading(false);
    }
  }

  function handleBack() {
    switch(step) {
      case 3: setStep(2); break;
      case 3.5: setStep(guestCount === 1 ? 2 : 3); break; // categories → guests
      case 4: setStep(3.5); break; // services → categories
      case 5: setStep(4); break; // optional add-ons → services
      case 6: { // needs removal? → guided flow, optional add-ons, or services
        const hasOptional = selectedServices.some(svc => (serviceAddOns[svc.id]?.optional || []).length > 0);
        setCurrentMandatoryIdx(0);
        if (useGuidedFlow || hasOptional) setStep(5);
        else setStep(4);
        break;
      }
      case 7: { // removal type / wax choice → depends on category
        if (selectedService && isWaxingService(selectedService)) {
          // Waxing skipped Step 6, go back to optional add-ons or services
          setCurrentMandatoryIdx(0);
          const hasOptional = selectedServices.some(svc => (serviceAddOns[svc.id]?.optional || []).length > 0);
          if (hasOptional) setStep(5);
          else setStep(4);
        } else {
          setStep(6); // nails → back to removal decision
        }
        break;
      }
      case 8: { // therapist → depends on what came before
        if (mandatoryServices.length > 0) {
          // Go back to last mandatory service's step
          const lastIdx = mandatoryServices.length - 1;
          setCurrentMandatoryIdx(lastIdx);
          setSelectedService(mandatoryServices[lastIdx]);
          // Waxing goes to Step 7 (wax choice), nails also goes to Step 7 (removal type)
          setStep(7);
        } else {
          // Only return to step 5 (Guided) when there's actually an add-on to show.
          // useGuidedFlow stays sticky-true after proceedFromServices, so don't
          // depend on it — Guided would render empty and immediately bounce back
          // to step 8 via shouldComplete → onComplete.
          const hasOptional = selectedServices.some(svc => (serviceAddOns[svc.id]?.optional || []).length > 0);
          if (hasOptional) setStep(5);
          else setStep(4);
        }
        break;
      }
      case 8.5: setStep(8); break; // cart review → therapist
      case 9: setStep(8.5); break;
      case 9.5: setStep(9); break;
      case 10: setReserveData(null); setInvoiceTotals(null); setUpkeepApplied(false); setStep(guestInfo ? 9 : 9.5); break;
      default: if (step > 1) setStep(step - 1);
    }
  }

  function handleBookAnother() {
    // Reset all booking state but keep guestInfo + selectedCenter
    setSelectedServices([]);
    setServiceAddOns({});
    setMandatoryServices([]);
    setCurrentMandatoryIdx(0);
    setTherapistsByService({});
    setSelectedTherapists({});
    setSelectedOptionalAddOns([]);
    setSelectedMandatoryAddOns([]);
    setNeedsRemoval(null);
    setServiceCart([]);
    setSelectedService(null);
    setSelectedTherapist(null);
    setSelectedCategory(null);
    setSelectedSubCategory('all');
    setExpandedAccordion(null);
    setAnatomyFilter(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setAvailableSlots([]);
    setRelaxedServices([]);
    setWeekStart(null);
    setBookingConfirmed(false);
    setBookingLoading(false);
    setBookingNotes('');
    setPolicyAgreed(false);
    setUnfilledForms([]);
    setReservationSeconds(null);
    setReserveData(null);
    setReserving(false);
    setInvoiceTotals(null);
    setUpkeepApplied(false);
    setCouponCode('');
    setGuestCount(1);
    setCurrentGuestIndex(0);
    setGuestSelections([]);
    setProceedingFromServices(false);
    setError(null);
    // Go back to location step (keeps guestInfo logged in)
    setStep(1);
    window.scrollTo(0, 0);
  }

  function getTotalForGuest(guest) {
    if (!guest?.services || guest.services.length === 0) return 0;
    let total = 0;
    guest.services.forEach(item => {
      total += getTherapistPrice(item.service, item.therapist);
      (item.optionalAddOns || []).forEach(a => { total += a.price || 0; });
      (item.mandatoryAddOns || []).forEach(a => { total += a.price || 0; });
    });
    return total;
  }

  function getCartTotal() {
    let total = 0;
    serviceCart.forEach(item => {
      total += getTherapistPrice(item.service, item.therapist);
      (item.optionalAddOns || []).forEach(a => { total += a.price || 0; });
      (item.mandatoryAddOns || []).forEach(a => { total += a.price || 0; });
    });
    return total;
  }

  // =====================
  // RENDER
  // =====================


  const Spinner = ({ text }) => (
    <div className="loading">
      <div className="spinner"></div>
      <span>{text || 'Loading...'}</span>
    </div>
  );

  const authFormProps = {
    selectedCenter, authMode, authPhone, authOtp, authFirstName, authLastName,
    authEmail, authDob, authGender, authReferralCode, authHowHeard,
    authPassword, authConfirmPassword, authMarketingEmail, authMarketingSms,
    authLoginUserName, authLoading, authError, showPassword,
    onAuthPhoneChange: setAuthPhone, onAuthOtpChange: setAuthOtp,
    onAuthFirstNameChange: setAuthFirstName, onAuthLastNameChange: setAuthLastName,
    onAuthEmailChange: setAuthEmail, onAuthDobChange: setAuthDob,
    onAuthGenderChange: setAuthGender, onAuthReferralCodeChange: setAuthReferralCode,
    onAuthHowHeardChange: setAuthHowHeard, onAuthPasswordChange: setAuthPassword,
    onAuthConfirmPasswordChange: setAuthConfirmPassword,
    onAuthMarketingEmailChange: setAuthMarketingEmail, onAuthMarketingSmsChange: setAuthMarketingSms,
    onShowPasswordToggle: () => setShowPassword(!showPassword),
    onSetAuthMode: setAuthMode, onSetAuthError: setAuthError, onSetAuthLoginUserName: setAuthLoginUserName,
    onPhoneSubmit: handlePhoneSubmit, onMultipleAccountsSubmit: handleMultipleAccountsSubmit,
    onOtpSubmit: handleOtpSubmit, onPasswordSubmit: handlePasswordSubmit,
    onForgotPassword: handleForgotPassword, onSignupSubmit: handleSignupSubmit,
    onSendOtp: handleSendOtp,
    resetCode, resetNewPassword, resetConfirmPassword, resetSuccess,
    onResetCodeChange: setResetCode, onResetNewPasswordChange: setResetNewPassword,
    onResetConfirmPasswordChange: setResetConfirmPassword,
    onResetPassword: handleResetPassword,
  };

  return (
    <div className="booking-container">
      {showQuickRebookBar && !guestInfo && step <= 4 && (
        <QuickRebookBar
          onSignIn={() => {
            setShowQuickRebookBar(false);
            setShowSignInModal(true);
            signInModalRef.current = true;
            window.history.pushState({ modal: 'signin' }, '');
            setSignInFromNav(true);
            setAuthMode('phone');
          }}
          onContinueAsGuest={() => {
            setShowQuickRebookBar(false);
            setStep(1);
          }}
          onDismiss={() => setShowQuickRebookBar(false)}
        />
      )}

      {/* RebookFlow removed — rebook now injects directly into booking flow */}

      {/* Quick Rebook Modal — appears when avatar is clicked or after login */}
      {showQuickRebookModal && guestInfo && (
        <div className="modal-overlay" onClick={() => setShowQuickRebookModal(false)}>
          <div className="quick-rebook-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowQuickRebookModal(false)}>✕</button>
            <h2 className="quick-rebook-modal-title">Welcome back, {guestInfo.firstName}!</h2>

            {accountLoading ? (
              <div className="loading-spinner" style={{ margin: '30px auto' }}></div>
            ) : accountData?.upcoming?.length > 0 && step <= 4 ? (
              <>
                <p className="quick-rebook-modal-subtitle">Your upcoming appointments</p>
                <div className="quick-rebook-modal-list">
                  {accountData.upcoming.slice(0, 3).map((appt, i) => (
                    <div key={i} className="upcoming-modal-card">
                      <div className="upcoming-modal-datetime">
                        {appt.startTime && new Date(appt.startTime).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                        {appt.startTime && ` ${new Date(appt.startTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`}
                      </div>
                      <div className="upcoming-modal-info">
                        {appt.service?.name || 'Appointment'}
                        {appt.therapist?.name ? ` with ${appt.therapist.name}` : ''}
                      </div>
                      <div className="upcoming-modal-actions">
                        <button className="upcoming-modal-btn upcoming-modal-btn-reschedule" onClick={() => {
                          setShowQuickRebookModal(false);
                          handleRebook(appt);
                        }}>RESCHEDULE</button>
                        <button className="upcoming-modal-btn upcoming-modal-btn-cancel" onClick={() => {
                          if (!window.confirm(`Cancel ${appt.service?.name || 'appointment'}?`)) return;
                          fetch(`${API_URL}/api/kiosk/cancel-appointment`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ invoiceId: appt.invoiceId, guestId: guestInfo.guestId })
                          })
                            .then(r => r.json())
                            .then(data => {
                              if (data.success) {
                                setAccountData(prev => ({
                                  ...prev,
                                  upcoming: (prev.upcoming || []).filter(a => a.invoiceId !== appt.invoiceId)
                                }));
                              } else {
                                alert(data.error || 'Could not cancel. Please call us.');
                              }
                            })
                            .catch(() => alert('Something went wrong. Please try again.'));
                        }}>CANCEL</button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : accountData?.recentVisits?.length > 0 ? (
              <>
                <p className="quick-rebook-modal-subtitle">Rebook a recent service</p>
                <div className="quick-rebook-modal-list">
                  {accountData.recentVisits.filter(v => v.status === 2 || v.status === undefined).slice(0, 3).map((visit, i) => (
                    <div key={i} className="quick-rebook-modal-card" onClick={() => {
                      setShowQuickRebookModal(false);
                      handleRebook(visit);
                    }}>
                      <div className="quick-rebook-modal-service">{visit.service?.name || 'Service'}</div>
                      <div className="quick-rebook-modal-details">
                        {visit.therapist?.name && <span>with {visit.therapist.name}</span>}
                        {visit.center?.name && <span>@ {visit.center.name}</span>}
                        {visit.date && <span>{new Date(visit.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                      </div>
                      <div className="quick-rebook-modal-btn">REBOOK</div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="quick-rebook-modal-subtitle">No upcoming appointments</p>
            )}

            <div className="quick-rebook-modal-actions">
              <button className="auth-button" onClick={() => { setShowQuickRebookModal(false); setStep(1); }}>
                BOOK SOMETHING NEW
              </button>
              <button className="auth-button quick-rebook-modal-secondary" onClick={() => { setShowQuickRebookModal(false); setStep(12); }}>
                VIEW ACCOUNT
              </button>
              <button className="quick-rebook-modal-signout" onClick={() => {
                setShowQuickRebookModal(false);
                setGuestInfo(null);
                setPolicyAgreed(false);
                setStep(1);
              }}>
                SIGN OUT
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sign In Modal — appears when "Sign In" is clicked from nav */}
      {showSignInModal && !guestInfo && (
        <div className="modal-overlay" onClick={closeSignInModal}>
          <div className="sign-in-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeSignInModal}>✕</button>
            <AuthForms variant="standalone" {...authFormProps} onBackToBooking={closeSignInModal} />
          </div>
        </div>
      )}

      <BookingHeader
        step={step}
        guestInfo={guestInfo}
        onAccountClick={() => { setShowQuickRebookModal(true); loadAccountData(); }}
        onSignInClick={() => { setShowSignInModal(true); signInModalRef.current = true; window.history.pushState({ modal: 'signin' }, ''); setSignInFromNav(true); setAuthMode('phone'); setAuthError(''); }}
        onBookClick={() => setStep(1)}
        onGiftCardClick={() => setStep(13)}
        onPackagesClick={() => setStep(14)}
        onReferralsClick={() => setStep(15)}
      />

      {step !== 13 && step !== 14 && step !== 15 && <ProgressIndicator step={step} guestCount={guestCount} onStepClick={setStep} />}

      <main className="booking-main">
        {error && (
          <div className="error-banner">
            {error}
            <button onClick={() => setError(null)} style={{ marginLeft: 10, cursor: 'pointer', background: 'none', border: 'none', fontWeight: 'bold' }}>✕</button>
          </div>
        )}

        {/* GROUP BOOKING FORM MODAL (7+ Guests, West Van only) */}
        {showGroupForm && (
          <div className="modal-overlay" onClick={() => setShowGroupForm(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setShowGroupForm(false)}>✕</button>
              <h2>Group Booking Inquiry</h2>
              <p className="modal-subtitle">5+ guests — We'll get back to you with availability and pricing</p>
              
              <form onSubmit={(e) => { e.preventDefault(); handleGroupFormSubmit(); }}>
                <div className="form-group">
                  <label>Your Name *</label>
                  <input type="text" value={groupFormName} onChange={(e) => setGroupFormName(e.target.value)} placeholder="Jane Doe" required />
                </div>

                <div className="form-group">
                  <label>Phone Number *</label>
                  <input type="tel" value={groupFormPhone} onChange={(e) => setGroupFormPhone(e.target.value)} placeholder="(604) 555-0123" required />
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={groupFormEmail} onChange={(e) => setGroupFormEmail(e.target.value)} placeholder="jane@email.com" />
                </div>

                <div className="form-group">
                  <label>Occasion</label>
                  <select value={groupFormOccasion} onChange={(e) => setGroupFormOccasion(e.target.value)}>
                    <option value="">Select occasion...</option>
                    <option value="Bachelorette">Bachelorette Party</option>
                    <option value="Girls Trip">Girls Trip</option>
                    <option value="Birthday">Birthday Party</option>
                    <option value="Corporate">Corporate Event</option>
                    <option value="Wedding">Wedding Party</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Number of Guests *</label>
                  <input type="number" value={groupFormGuestCount} onChange={(e) => setGroupFormGuestCount(e.target.value)} placeholder="10" min="5" required />
                </div>

                <div className="form-group">
                  <label>Preferred Date & Time</label>
                  <input type="text" value={groupFormTime} onChange={(e) => setGroupFormTime(e.target.value)} placeholder="e.g., Saturday, March 15th, 2pm" />
                </div>

                <div className="form-group">
                  <label>Services Interested In</label>
                  <textarea value={groupFormServices} onChange={(e) => setGroupFormServices(e.target.value)} placeholder="e.g., Brazilian waxing, nail service, etc." rows="3"></textarea>
                </div>

                <button type="submit" className="form-submit-btn" disabled={groupFormSubmitting}>
                  {groupFormSubmitting ? 'Sending...' : 'Send Inquiry'}
                </button>
              </form>
              
              <p className="form-note">We'll get back to you {groupFormEmail ? 'via email' : groupFormPhone ? `at ${groupFormPhone}` : ''} to confirm details and pricing.</p>
            </div>
          </div>
        )}

        {/* STEP 1: Location */}
        {step === 1 && (
          <LocationStep centers={centers} selectedCenter={selectedCenter} onCenterSelect={handleCenterSelect} />
        )}

        {/* STEP 2: Guest Count */}
        {step === 2 && (
          <GuestCountStep selectedCenter={selectedCenter} guestCount={guestCount} onGuestCountSelect={handleGuestCountSelect} />
        )}

        {/* STEP 3.5: Category Grid */}
        {step === 3.5 && (
          <CategoryGridStep
            selectedCenter={selectedCenter}
            categoriesData={categories}
            allCenterServices={allCenterServices}
            onCategoryTap={handleCategoryTap}
            anatomy={anatomyFilter}
            onAnatomySet={setAnatomyFilter}
            selectedServices={selectedServices}
            onToggleService={toggleService}
          />
        )}

        {step === 3 && (
          <GuestListStep selectedCenter={selectedCenter} guestSelections={guestSelections} guestInfo={guestInfo} getTherapistPrice={getTherapistPrice} onAddServicesForGuest={handleAddServicesForGuest} onCopyServicesToAll={handleCopyServicesToAll} onGuestInfoChange={handleGuestInfoChange} />
        )}

        {/* STEP 4: Service Selection (toggle-to-cart) */}
        {step === 4 && (
          <ServiceSelectionStep
            guestCount={guestCount} currentGuestIndex={currentGuestIndex} selectedCenter={selectedCenter}
            loading={loading} categories={categories} selectedCategory={selectedCategory} onCategorySelect={handleCategorySelect}
            loadingCategoryServices={loadingCategoryServices} filteredServices={getFilteredServices()}
            subCategories={getSubCategories()} displayServices={getDisplayServices()}
            selectedSubCategory={selectedSubCategory} onSubCategorySelect={setSelectedSubCategory}
            onShowDetail={setDetailModalService}
            serviceSearch={serviceSearch} onSearchChange={handleServiceSearch}
            serviceGroups={getServiceGroups()} searchResults={getSearchResults()} searchLoading={!allCenterServices}
            selectedServices={selectedServices} onToggleService={toggleService}
            proceedingFromServices={proceedingFromServices}
            needsAnatomy={isAnatomyCategory(selectedCategory)}
            anatomy={anatomyFilter}
            onAnatomyChange={setAnatomyFilter}
            allCenterServices={allCenterServices}
            onBackToCategories={() => setStep(3.5)}
          />
        )}

        {/* STEP 5: Optional Add-Ons — always Guided */}
        {step === 5 && (
          <GuidedAddOnsFlow
            selectedServices={addOnServices}
            serviceAddOns={serviceAddOns}
            nailServiceIds={new Set(addOnServices.filter(s => isNailService(s)).map(s => s.id))}
            waxServiceIds={new Set(addOnServices.filter(s => isWaxingService(s)).map(s => s.id))}
            selectedOptionalAddOns={selectedOptionalAddOns}
            onToggleAddOn={toggleOptionalAddOn}
            onComplete={handleGuidedFlowComplete}
            onBack={handleBack}
            getCategoryForService={getCategoryForService}
          />
        )}

        {/* STEP 6: Do you need removal? (iterates per mandatory service) */}
        {step === 6 && selectedService && (
          <RemovalDecisionStep currentService={selectedService} selectedCenter={selectedCenter} needsRemoval={needsRemoval} onRemovalDecision={handleRemovalDecision} onBack={handleBack} />
        )}

        {/* STEP 7: Select Removal Type (for current mandatory service) */}
        {step === 7 && selectedService && (
          <RemovalTypeStep currentService={selectedService} serviceAddOns={serviceAddOns} selectedCenter={selectedCenter} selectedMandatoryAddOns={selectedMandatoryAddOns} onToggleAddOn={toggleMandatoryAddOn} serviceCategory={selectedService ? getCategoryForService(selectedService.id) : ''} onSkipAddOn={advanceMandatory} selectedOptionalAddOns={selectedOptionalAddOns} onToggleOptionalAddOn={toggleOptionalAddOn} onBack={handleBack} onContinue={advanceMandatory} />
        )}

        {/* STEP 8: Therapist Selection (combined per-service picker) */}
        {step === 8 && (
          <TherapistStep selectedServices={selectedServices} selectedCenter={selectedCenter} therapistsByService={therapistsByService} loadingTherapists={loadingTherapists} selectedTherapists={selectedTherapists} onTherapistChange={(svcId, therapist) => setSelectedTherapists(prev => ({ ...prev, [svcId]: therapist }))} selectedOptionalAddOns={selectedOptionalAddOns} selectedMandatoryAddOns={selectedMandatoryAddOns} />
        )}

        {/* STEP 8.5: Service Summary — Add Another? */}
        {step === 8.5 && (
          <CartSummaryStep selectedCenter={selectedCenter} serviceCart={serviceCart} getTherapistPrice={getTherapistPrice} getCartTotal={getCartTotal} onRemoveCartItem={handleRemoveCartItem} onAddMoreServices={handleAddAnotherService} guestCount={guestCount} currentGuestIndex={currentGuestIndex} />
        )}

        {/* STEP 9: Time Selection */}
        {step === 9 && (
          <TimeSelectionStep
            selectedCenter={selectedCenter} weekStart={weekStart} selectedDate={selectedDate}
            selectedTime={selectedTime} loadingSlots={loadingSlots} availableSlots={availableSlots}
            showAutoAdvanceToast={showAutoAdvanceToast} autoAdvanceDate={autoAdvanceDate}
            onDismissToast={() => setShowAutoAdvanceToast(false)}
            onWeekNav={handleWeekNav} onDateSelect={handleDateSelect} onTimeSelect={handleTimeSelect}
            getDayAvailability={getDayAvailability}
            upkeepEligibility={upkeepEligibility}
            preferencedServices={getPreferencedServices()}
            relaxedServiceLabels={getRelaxedServiceLabels()}
            onRelaxService={handleRelaxService}
            onRelaxAllServices={handleRelaxAllServices}
            onResetRelaxed={handleResetRelaxed}
          />
        )}

        {/* STEP 9.5: Sign In / Sign Up */}
        {step === 9.5 && (
          <AuthForms variant="booking" {...authFormProps} />
        )}

        {/* STEP 10: Confirmation */}
        {step === 10 && (
          <ConfirmStep
            selectedCenter={selectedCenter} selectedDate={selectedDate} selectedTime={selectedTime}
            guestInfo={guestInfo} guestCount={guestCount} guestSelections={guestSelections}
            getTherapistPrice={getTherapistPrice} getTotalForGuest={getTotalForGuest}
            bookingNotes={bookingNotes} onBookingNotesChange={setBookingNotes}
            reservationSeconds={reservationSeconds}
            onReservationExpired={() => {
              // Cancel the expired reservation in Zenoti
              if (reserveData?.invoiceId) {
                fetch(`${API_URL}/api/booking/cancel-expired`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ invoiceId: reserveData.invoiceId })
                }).catch(() => {});
              }
              setStep(9); setSelectedTime(null); setReserveData(null); setInvoiceTotals(null);
            }}
            policyAgreed={policyAgreed} onPolicyChange={setPolicyAgreed}
            setStep={(s) => {
              // Cancel reservation in Zenoti when navigating away from confirm page
              if (reserveData?.invoiceId) {
                fetch(`${API_URL}/api/booking/cancel-expired`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ invoiceId: reserveData.invoiceId })
                }).catch(() => {});
              }
              setReserveData(null); setInvoiceTotals(null); setUpkeepApplied(false); setStep(s);
            }}
            upkeepEligibility={upkeepEligibility}
            couponCode={couponCode} onCouponCodeChange={setCouponCode}
            onApplyCoupon={applyCouponCode}
            invoiceTotals={invoiceTotals}
            upkeepApplied={upkeepApplied}
            reserving={reserving}
          />
        )}

        {/* STEP 11: Booking Confirmed */}
        {step === 11 && (
          <SuccessStep selectedCenter={selectedCenter} selectedDate={selectedDate} selectedTime={selectedTime} guestInfo={guestInfo} guestSelections={guestSelections} unfilledForms={unfilledForms} onBookAnother={handleBookAnother} upkeepApplied={upkeepApplied} />
        )}

        {/* STEP 12: Sign In (not logged in) */}
        {step === 12 && !guestInfo && (
          <AuthForms variant="standalone" {...authFormProps} onBackToBooking={() => setStep(1)} />
        )}

        {step === 12 && guestInfo && (
          <AccountPage
            guestInfo={guestInfo}
            accountData={accountData}
            accountLoading={accountLoading}
            accountTab={accountTab}
            onTabChange={setAccountTab}
            onSetAccountData={setAccountData}
            onLoadFullAccountData={() => loadAccountData(null, true)}
            onBackToBooking={() => setStep(1)}
            onRebookClick={(appointment) => {
              handleRebook(appointment);
            }}
            onLogout={() => { setGuestInfo(null); setPolicyAgreed(false); setStep(1); }}
            selectedCenter={selectedCenter}
          />
        )}

        {/* STEP 13: Gift Card Purchase */}
        {step === 13 && (
          <GiftCardPage onBackToBooking={() => setStep(1)} guestInfo={guestInfo} authFormProps={authFormProps} />
        )}

        {/* STEP 14: Package Purchase */}
        {step === 14 && (
          <PackagesPage
            onBackToBooking={() => setStep(1)}
            guestInfo={guestInfo}
            authFormProps={authFormProps}
            setSelectedCenter={setSelectedCenter}
          />
        )}

        {/* STEP 15: Referrals */}
        {step === 15 && (
          <ReferralsPage
            onBookNow={() => setStep(1)}
            onAccountClick={() => setStep(12)}
            guestInfo={guestInfo}
          />
        )}
      </main>

      {/* Service Detail Modal — onBook toggles service into cart instead of navigating */}
      <ServiceDetailModal service={detailModalService} onClose={() => setDetailModalService(null)} onBook={(svc) => {
        const isAdding = !selectedServices.some(s => s.id === svc.id);
        const hasConflict = isAdding && (
          (isSprayTan(svc) && selectedServices.some(s => isLashOrBrow(s))) ||
          (isLashOrBrow(svc) && selectedServices.some(s => isSprayTan(s)))
        );
        toggleService(svc);
        if (!hasConflict) setDetailModalService(null);
      }} />

      {/* Conflict warning modal */}
      {conflictWarning && (
        <div className="bf-modal-overlay" onClick={() => setConflictWarning(null)}>
          <div className="bf-modal" onClick={e => e.stopPropagation()}>
            <p style={{ margin: '0 0 20px', fontSize: 15, lineHeight: 1.5 }}>{conflictWarning}</p>
            <button className="bf-modal-btn" onClick={() => setConflictWarning(null)}>GOT IT</button>
          </div>
        </div>
      )}

      <StickyNav
        step={step}
        serviceCart={serviceCart}
        selectedOptionalAddOns={selectedOptionalAddOns}
        selectedMandatoryAddOns={selectedMandatoryAddOns}
        selectedTime={selectedTime}
        guestInfo={guestInfo}
        guestSelections={guestSelections}
        bookingConfirmed={bookingConfirmed}
        bookingLoading={bookingLoading}
        policyAgreed={policyAgreed}
        onBack={handleBack}
        onGuestListNext={() => {
          const allComplete = guestSelections.every(g => g.services && g.services.length > 0);
          const allInfoFilled = guestSelections.every((g, idx) => idx === 0 || (g.firstName?.trim() && g.lastName?.trim() && g.phone?.trim()));
          if (allComplete && allInfoFilled) setStep(9);
        }}
        selectedServices={selectedServices}
        selectedTherapists={selectedTherapists}
        onProceedFromServices={proceedFromServices}
        proceedingFromServices={proceedingFromServices}
        onMandatoryAddOnsContinue={handleMandatoryAddOnsContinue}
        isWaxingStep={step === 7 && selectedService && isWaxingService(selectedService)}
        onBuildCartAndContinue={buildCartAndContinue}
        onContinueToTime={handleContinueToTime}
        onTimeNext={() => {
          if (guestInfo) {
            setStep(10);
          } else {
            setAuthMode('phone');
            setAuthError('');
            setAuthOtp('');
            setStep(9.5);
          }
        }}
        onConfirmBooking={handleConfirmBooking}
        reserving={reserving}
        onGuestCountDefault={() => handleGuestCountSelect(1)}
      />
    </div>
  );
}

export default BookingFlow;
